import {
  type ChangeEvent,
  type CSSProperties,
  type DragEvent,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop';
import { createPortal } from 'react-dom';
import {
  Archive,
  Activity,
  ArrowRight,
  ArrowLeft,
  BadgeCheck,
  BarChart3,
  ChevronDown,
  CheckCircle2,
  Cookie,
  Crop as CropIcon,
  Download,
  Eye,
  FileAudio,
  FileImage,
  FileText,
  FileVideo,
  Files,
  KeyRound,
  Languages,
  Link2,
  Loader2,
  Maximize2,
  Moon,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  TrendingUp,
  UploadCloud,
  Users,
  X,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { languages, translate } from './data/i18n';
import { KimAnalyticsService } from './services/analytics/KimAnalyticsService';
import { YandexMetrikaService } from './services/analytics/YandexMetrikaService';
import { DashboardAnalyticsService, type DashboardAnalyticsSummary } from './services/dashboard/DashboardAnalyticsService';
import { DashboardAccessService } from './services/dashboard/DashboardAccessService';
import { DownloadService } from './services/downloadService';
import { FileConversionService, type ConversionOptions, type ConversionProfileId, type SizeEstimate } from './services/files/FileConversionService';
import { ImageProcessor } from './services/image/ImageProcessor';
import { ConsentService, type ConsentPreferences } from './services/privacy/ConsentService';
import { HostRoutingService, type ApplicationSurface } from './services/routing/HostRoutingService';
import type { ConversionCategory, ConversionProfile, CropPreset, ImageOutputFormat, LanguageCode, ProcessedFile, ThemeMode } from './types';
import { formatFileSize } from './utils/fileSize';

const imageFormats: Array<{ value: ImageOutputFormat; label: string }> = [
  { value: 'image/png', label: 'PNG' },
  { value: 'image/jpeg', label: 'JPEG' },
  { value: 'image/avif', label: 'AVIF' },
  { value: 'image/webp', label: 'WebP' },
];

const cropPresets: CropPreset[] = ['free', '1:1', '4:3', '16:9', '3:4', '9:16'];

const aspectByPreset: Record<CropPreset, number | undefined> = {
  free: undefined,
  '1:1': 1,
  '4:3': 4 / 3,
  '16:9': 16 / 9,
  '3:4': 3 / 4,
  '9:16': 9 / 16,
};

const imageProcessor = new ImageProcessor();
const fileConversionService = new FileConversionService();
const downloadService = new DownloadService();
const consentService = new ConsentService();
const kimAnalyticsService = new KimAnalyticsService();
const yandexMetrikaService = new YandexMetrikaService();
const hostRoutingService = new HostRoutingService();
const dashboardAccessService = new DashboardAccessService();
const dashboardAnalyticsService = new DashboardAnalyticsService();
const supportedLanguageCodes = new Set(languages.map((item) => item.code));
const siteUrl = 'https://kim-converter.ru';
const revealedElements = new WeakSet<HTMLElement>();

function trackAnalyticsEvent(name: string, metadata?: Record<string, string | number | boolean | null>): void {
  yandexMetrikaService.reachGoal(name, metadata);
  kimAnalyticsService.trackEvent(name, { metadata });
}

type AppRoute = 'home' | 'images' | 'files' | 'imageInfo' | 'fileInfo' | 'privacy';

interface PendingUpload {
  id: string;
  files: File[];
}

interface ConversionPair {
  from: string;
  to: string;
}

type DashboardAccessState =
  | { status: 'loading'; ip: null; message: null }
  | { status: 'blocked'; ip: string; message: null }
  | { status: 'ready'; ip: string; message: null }
  | { status: 'authenticated'; ip: string; message: null }
  | { status: 'error'; ip: string | null; message: string };

interface IndexedConversionPair extends ConversionPair {
  category: ConversionCategory;
}

const indexedConversionPairs: IndexedConversionPair[] = [
  ...buildPairs('image', ['png', 'jpeg', 'jpg', 'avif', 'webp', 'bmp', 'eps', 'gif', 'ico', 'odd', 'psd', 'svg', 'tga', 'tif', 'tiff'], [
    'png',
    'jpeg',
    'jpg',
    'avif',
    'webp',
    'bmp',
    'eps',
    'gif',
    'ico',
    'odd',
    'psd',
    'svg',
    'tga',
    'tiff',
  ]),
  ...buildPairs('audio', ['aac', 'aiff', 'alac', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'wma'], ['aac', 'flac', 'm4a', 'mp3', 'ogg', 'wav']),
  ...buildPairs('video', ['avi', 'gif', 'mkv', 'mov', 'mp4', 'mpeg', 'mpg', 'webm'], ['gif', 'mp4', 'webm']),
  ...buildPairs('document', ['doc', 'docx', 'pdf', 'ps', 'text', 'txt', 'word'], ['doc', 'docx', 'ps', 'txt', 'word']),
  ...buildPairs('document', ['text', 'txt'], ['pdf']),
  ...buildPairs('data', ['csv', 'json'], ['csv', 'json']),
  ...buildPairs('archive', ['aac', 'csv', 'doc', 'docx', 'gif', 'jpg', 'json', 'm4a', 'mp3', 'mp4', 'pdf', 'png', 'txt', 'webm', 'webp'], ['zip']),
];

const indexedConversionPairSlugs = new Set(indexedConversionPairs.map((pair) => getConversionPairSlug(pair)));

const routePaths: Record<AppRoute, string> = {
  home: '/',
  images: '/image-editor',
  files: '/file-converter',
  imageInfo: '/tools/image-editor',
  fileInfo: '/tools/file-converter',
  privacy: '/privacy',
};

type RouteSeo = {
  title: string;
  description: string;
};

interface SeoFaqItem {
  question: string;
  answer: string;
}

interface PopularConversionLink {
  slug: string;
  label: string;
  description: Record<LanguageCode, string>;
}

interface ConversionSeoCopy {
  eyebrow: string;
  title: (from: string, to: string) => string;
  lead: (from: string, to: string) => string;
  fileHandlingTitle: string;
  fileHandling: (from: string, to: string) => string;
  stepsTitle: string;
  steps: (from: string, to: string) => string[];
  relatedTitle: string;
}

const routeSeo: Record<LanguageCode, Record<AppRoute, RouteSeo>> = {
  ru: {
    home: {
      title: 'Kim Converter - конвертер файлов и изображений онлайн',
      description:
        'Бесплатный онлайн-конвертер файлов и изображений: меняйте размер, обрезайте фото и конвертируйте PNG, JPEG, AVIF, WebP, CSV, JSON и ZIP прямо в браузере.',
    },
    images: {
      title: 'Редактор изображений онлайн - resize, crop, PNG, JPEG, AVIF, WebP',
      description:
        'Редактируйте изображения онлайн: меняйте размер, обрезайте кадр, сохраняйте пропорции и экспортируйте результат в PNG, JPEG, PNG или AVIF.',
    },
    files: {
      title: 'Конвертер файлов онлайн - изображения, CSV, JSON и ZIP',
      description:
        'Конвертируйте файлы в браузере: изображения в PNG, JPEG, PNG и AVIF, JSON в CSV, CSV в JSON, файлы в ZIP без очередей и водяных знаков.',
    },
    imageInfo: {
      title: 'Редактирование изображений онлайн - Kim Converter',
      description:
        'Информация о редакторе изображений Kim Converter: обрезка, изменение размера, сохранение пропорций и экспорт в PNG, JPEG, PNG или AVIF.',
    },
    fileInfo: {
      title: 'Конвертация файлов онлайн - Kim Converter',
      description:
        'Информация о конвертере файлов Kim Converter: конвертация изображений, документов, таблиц, аудио, видео и упаковка файлов в ZIP.',
    },
    privacy: {
      title: 'Политика конфиденциальности - Kim Converter',
      description:
        'Как Kim Converter обрабатывает файлы, localStorage, cookies, аналитику и рекламные технологии. Управление согласием на аналитику и рекламу.',
    },
  },
  en: {
    home: {
      title: 'Kim Converter - online file and image converter',
      description:
        'Free online file and image converter: resize photos, crop images and convert PNG, JPEG, AVIF, WebP, CSV, JSON and ZIP directly in your browser.',
    },
    images: {
      title: 'Online image editor - resize, crop, PNG, JPEG, AVIF, WebP',
      description:
        'Edit images online: resize, crop, keep proportions and export the result as PNG, JPEG, AVIF or WebP without a watermark.',
    },
    files: {
      title: 'Online file converter - images, CSV, JSON and ZIP',
      description:
        'Convert files in your browser: images to PNG, JPEG, PNG and AVIF, JSON to CSV, CSV to JSON and files to ZIP without queues.',
    },
    imageInfo: {
      title: 'Online image editing - Kim Converter',
      description:
        'Learn about Kim Converter image editing: crop, resize, keep proportions and export as PNG, JPEG, AVIF or WebP.',
    },
    fileInfo: {
      title: 'Online file conversion - Kim Converter',
      description:
        'Learn about Kim Converter file conversion: convert images, documents, tables, audio, video and package files into ZIP.',
    },
    privacy: {
      title: 'Privacy Policy - Kim Converter',
      description:
        'How Kim Converter handles files, localStorage, cookies, analytics and advertising technologies, including consent controls.',
    },
  },
  de: {
    home: {
      title: 'Kim Converter - Online-Konverter für Dateien und Bilder',
      description:
        'Kostenloser Online-Konverter: Bilder zuschneiden, Größe ändern und PNG, JPEG, AVIF, WebP, CSV, JSON und ZIP direkt im Browser konvertieren.',
    },
    images: {
      title: 'Bildbearbeitung online - Größe ändern, zuschneiden, PNG, JPEG, AVIF, WebP',
      description:
        'Bearbeiten Sie Bilder online: Größe ändern, zuschneiden, Proportionen behalten und als PNG, JPEG, AVIF oder WebP exportieren.',
    },
    files: {
      title: 'Dateikonverter online - Bilder, CSV, JSON und ZIP',
      description:
        'Konvertieren Sie Dateien im Browser: Bilder in PNG, JPEG, PNG und AVIF, JSON in CSV, CSV in JSON und Dateien in ZIP.',
    },
    imageInfo: {
      title: 'Bildbearbeitung online - Kim Converter',
      description:
        'Informationen zur Bildbearbeitung in Kim Converter: zuschneiden, Größe ändern, Proportionen behalten und als PNG, JPEG, AVIF oder WebP exportieren.',
    },
    fileInfo: {
      title: 'Dateikonvertierung online - Kim Converter',
      description:
        'Informationen zur Dateikonvertierung in Kim Converter: Bilder, Dokumente, Tabellen, Audio, Video konvertieren und Dateien als ZIP packen.',
    },
    privacy: {
      title: 'Datenschutzerklärung - Kim Converter',
      description:
        'Wie Kim Converter Dateien, localStorage, Cookies, Analyse- und Werbetechnologien verarbeitet und Zustimmung verwaltet.',
    },
  },
  el: {
    home: {
      title: 'Kim Converter - online μετατροπέας αρχείων και εικόνων',
      description:
        'Δωρεάν online μετατροπέας: αλλάξτε μέγεθος, περικόψτε εικόνες και μετατρέψτε PNG, JPEG, AVIF, WebP, CSV, JSON και ZIP στο πρόγραμμα περιήγησης.',
    },
    images: {
      title: 'Online επεξεργασία εικόνων - resize, crop, PNG, JPEG, AVIF, WebP',
      description:
        'Επεξεργαστείτε εικόνες online: αλλαγή μεγέθους, περικοπή, διατήρηση αναλογιών και εξαγωγή σε PNG, JPEG, PNG ή AVIF.',
    },
    files: {
      title: 'Online μετατροπέας αρχείων - εικόνες, CSV, JSON και ZIP',
      description:
        'Μετατρέψτε αρχεία στο πρόγραμμα περιήγησης: εικόνες σε PNG, JPEG, PNG και AVIF, JSON σε CSV, CSV σε JSON και αρχεία σε ZIP.',
    },
    imageInfo: {
      title: 'Online επεξεργασία εικόνων - Kim Converter',
      description:
        'Πληροφορίες για την επεξεργασία εικόνων στο Kim Converter: περικοπή, αλλαγή μεγέθους, αναλογίες και εξαγωγή σε PNG, JPEG, PNG ή AVIF.',
    },
    fileInfo: {
      title: 'Online μετατροπή αρχείων - Kim Converter',
      description:
        'Πληροφορίες για τη μετατροπή αρχείων στο Kim Converter: εικόνες, έγγραφα, πίνακες, ήχος, βίντεο και ZIP.',
    },
    privacy: {
      title: 'Πολιτική απορρήτου - Kim Converter',
      description:
        'Πώς το Kim Converter χειρίζεται αρχεία, localStorage, cookies, analytics, διαφημιστικές τεχνολογίες και συγκατάθεση.',
    },
  },
  es: {
    home: {
      title: 'Kim Converter - convertidor online de archivos e imágenes',
      description:
        'Convertidor online gratuito: cambia el tamaño, recorta imágenes y convierte PNG, JPEG, AVIF, WebP, CSV, JSON y ZIP directamente en el navegador.',
    },
    images: {
      title: 'Editor de imágenes online - resize, crop, PNG, JPEG, AVIF, WebP',
      description:
        'Edita imágenes online: cambia el tamaño, recorta, conserva proporciones y exporta en PNG, JPEG, AVIF o WebP sin marca de agua.',
    },
    files: {
      title: 'Convertidor de archivos online - imágenes, CSV, JSON y ZIP',
      description:
        'Convierte archivos en el navegador: imágenes a PNG, JPEG, PNG y AVIF, JSON a CSV, CSV a JSON y archivos a ZIP.',
    },
    imageInfo: {
      title: 'Edición de imágenes online - Kim Converter',
      description:
        'Información sobre la edición de imágenes en Kim Converter: recorte, cambio de tamaño, proporciones y exportación a PNG, JPEG, AVIF o WebP.',
    },
    fileInfo: {
      title: 'Conversión de archivos online - Kim Converter',
      description:
        'Información sobre la conversión de archivos en Kim Converter: imágenes, documentos, tablas, audio, vídeo y ZIP.',
    },
    privacy: {
      title: 'Política de privacidad - Kim Converter',
      description:
        'Cómo Kim Converter gestiona archivos, localStorage, cookies, analítica, tecnologías publicitarias y consentimiento.',
    },
  },
  fr: {
    home: {
      title: 'Kim Converter - convertisseur de fichiers et images en ligne',
      description:
        'Convertisseur en ligne gratuit: redimensionnez, recadrez et convertissez PNG, JPEG, AVIF, WebP, CSV, JSON et ZIP directement dans le navigateur.',
    },
    images: {
      title: "Éditeur d'images en ligne - resize, crop, PNG, JPEG, AVIF, WebP",
      description:
        "Modifiez vos images en ligne: redimensionnement, recadrage, conservation des proportions et export en PNG, JPEG, AVIF ou WebP.",
    },
    files: {
      title: 'Convertisseur de fichiers en ligne - images, CSV, JSON et ZIP',
      description:
        'Convertissez des fichiers dans le navigateur: images en PNG, JPEG, PNG et AVIF, JSON en CSV, CSV en JSON et fichiers en ZIP.',
    },
    imageInfo: {
      title: 'Édition d’images en ligne - Kim Converter',
      description:
        'Informations sur l’édition d’images dans Kim Converter: recadrage, redimensionnement, proportions et export en PNG, JPEG, AVIF ou WebP.',
    },
    fileInfo: {
      title: 'Conversion de fichiers en ligne - Kim Converter',
      description:
        'Informations sur la conversion de fichiers dans Kim Converter: images, documents, tableaux, audio, vidéo et ZIP.',
    },
    privacy: {
      title: 'Politique de confidentialité - Kim Converter',
      description:
        'Comment Kim Converter gère les fichiers, localStorage, cookies, analytics, technologies publicitaires et consentement.',
    },
  },
};

const ogLocales: Record<LanguageCode, string> = {
  ru: 'ru_RU',
  en: 'en_US',
  de: 'de_DE',
  el: 'el_GR',
  es: 'es_ES',
  fr: 'fr_FR',
};

const popularConversionLinks: PopularConversionLink[] = [
  {
    slug: 'png-to-jpg',
    label: 'PNG to JPG',
    description: {
      ru: 'Конвертировать PNG в JPG онлайн без водяных знаков.',
      en: 'Convert PNG to JPG online without watermarks.',
      de: 'PNG online ohne Wasserzeichen in JPG umwandeln.',
      el: 'Μετατρέψτε PNG σε JPG online χωρίς υδατογράφημα.',
      es: 'Convierte PNG a JPG online sin marca de agua.',
      fr: 'Convertir PNG en JPG en ligne sans filigrane.',
    },
  },
  {
    slug: 'jpg-to-png',
    label: 'JPG to PNG',
    description: {
      ru: 'Преобразовать JPG в PNG прямо в браузере.',
      en: 'Convert JPG to PNG directly in the browser.',
      de: 'JPG direkt im Browser in PNG konvertieren.',
      el: 'Μετατρέψτε JPG σε PNG απευθείας στο πρόγραμμα περιήγησης.',
      es: 'Convierte JPG a PNG directamente en el navegador.',
      fr: 'Convertir JPG en PNG directement dans le navigateur.',
    },
  },
  {
    slug: 'webp-to-jpg',
    label: 'WebP to JPG',
    description: {
      ru: 'Быстро перевести WebP в JPG для сайтов и документов.',
      en: 'Quickly change WebP to JPG for websites and documents.',
      de: 'WebP schnell in JPG für Websites und Dokumente umwandeln.',
      el: 'Γρήγορη αλλαγή WebP σε JPG για ιστότοπους και έγγραφα.',
      es: 'Pasa WebP a JPG para sitios web y documentos.',
      fr: 'Passer rapidement WebP en JPG pour sites et documents.',
    },
  },
  {
    slug: 'png-to-webp',
    label: 'PNG to WebP',
    description: {
      ru: 'Сжать PNG и экспортировать изображение в WebP.',
      en: 'Compress PNG and export the image as WebP.',
      de: 'PNG komprimieren und als WebP exportieren.',
      el: 'Συμπιέστε PNG και εξαγάγετε την εικόνα σε WebP.',
      es: 'Comprime PNG y exporta la imagen como WebP.',
      fr: 'Compresser PNG et exporter l’image en WebP.',
    },
  },
  {
    slug: 'json-to-csv',
    label: 'JSON to CSV',
    description: {
      ru: 'Преобразовать JSON в CSV для таблиц и импорта данных.',
      en: 'Convert JSON to CSV for spreadsheets and data imports.',
      de: 'JSON für Tabellen und Datenimporte in CSV umwandeln.',
      el: 'Μετατρέψτε JSON σε CSV για πίνακες και εισαγωγή δεδομένων.',
      es: 'Convierte JSON a CSV para hojas de cálculo e importación.',
      fr: 'Convertir JSON en CSV pour tableaux et imports de données.',
    },
  },
  {
    slug: 'csv-to-json',
    label: 'CSV to JSON',
    description: {
      ru: 'Конвертировать CSV в JSON онлайн.',
      en: 'Convert CSV to JSON online.',
      de: 'CSV online in JSON konvertieren.',
      el: 'Μετατρέψτε CSV σε JSON online.',
      es: 'Convierte CSV a JSON online.',
      fr: 'Convertir CSV en JSON en ligne.',
    },
  },
  {
    slug: 'mp3-to-wav',
    label: 'MP3 to WAV',
    description: {
      ru: 'Подготовить WAV из MP3 без очереди.',
      en: 'Create WAV from MP3 without queues.',
      de: 'WAV aus MP3 ohne Warteschlange erstellen.',
      el: 'Δημιουργήστε WAV από MP3 χωρίς αναμονή.',
      es: 'Crea WAV desde MP3 sin colas.',
      fr: 'Créer un WAV depuis MP3 sans file d’attente.',
    },
  },
  {
    slug: 'wav-to-mp3',
    label: 'WAV to MP3',
    description: {
      ru: 'Сделать MP3 из WAV и уменьшить размер файла.',
      en: 'Create MP3 from WAV and reduce file size.',
      de: 'MP3 aus WAV erstellen und Dateigröße reduzieren.',
      el: 'Δημιουργήστε MP3 από WAV και μειώστε το μέγεθος.',
      es: 'Crea MP3 desde WAV y reduce el tamaño del archivo.',
      fr: 'Créer un MP3 depuis WAV et réduire la taille du fichier.',
    },
  },
];

const conversionSeoCopy: Record<LanguageCode, ConversionSeoCopy> = {
  ru: {
    eyebrow: 'Практичная конвертация',
    title: (from, to) => `${from} в ${to}: конвертация файлов онлайн`,
    lead: (from, to) =>
      `Загрузите ${from}-файл, выберите доступные параметры и скачайте результат в формате ${to} без водяных знаков.`,
    fileHandlingTitle: 'Обработка файлов',
    fileHandling: (from, to) =>
      `Если формат ${from} поддерживается браузером, конвертация в ${to} выполняется локально. Серверные форматы отправляются в API только на время обработки.`,
    stepsTitle: 'Как выполнить конвертацию',
    steps: (from, to) => [
      `Загрузите файл ${from} с устройства или по ссылке.`,
      `Выберите профиль ${to}, качество или сжатие, если эти настройки доступны.`,
      `Запустите конвертацию и скачайте готовый ${to} файл.`,
    ],
    relatedTitle: 'Похожие конвертации',
  },
  en: {
    eyebrow: 'Practical conversion',
    title: (from, to) => `${from} to ${to} online file conversion`,
    lead: (from, to) =>
      `Upload a ${from} file, adjust the available options and download the finished ${to} result without watermarks.`,
    fileHandlingTitle: 'File handling',
    fileHandling: (from, to) =>
      `When ${from} is browser-supported, the ${to} conversion runs locally. Server-only formats are sent to the API only for the conversion job.`,
    stepsTitle: 'How to convert the file',
    steps: (from, to) => [
      `Upload a ${from} file from your device or by URL.`,
      `Choose the ${to} profile, quality or compression when available.`,
      `Start conversion and download the finished ${to} file.`,
    ],
    relatedTitle: 'Related conversions',
  },
  de: {
    eyebrow: 'Praktische Konvertierung',
    title: (from, to) => `${from} in ${to} online konvertieren`,
    lead: (from, to) =>
      `Laden Sie eine ${from}-Datei hoch, passen Sie verfügbare Optionen an und laden Sie das fertige ${to}-Ergebnis ohne Wasserzeichen herunter.`,
    fileHandlingTitle: 'Dateiverarbeitung',
    fileHandling: (from, to) =>
      `Wenn ${from} im Browser unterstützt wird, läuft die Konvertierung in ${to} lokal. Reine Serverformate werden nur für den Auftrag an die API gesendet.`,
    stepsTitle: 'So funktioniert die Konvertierung',
    steps: (from, to) => [
      `Laden Sie eine ${from}-Datei vom Gerät oder per URL hoch.`,
      `Wählen Sie das ${to}-Profil sowie Qualität oder Komprimierung, falls verfügbar.`,
      `Starten Sie die Konvertierung und laden Sie die fertige ${to}-Datei herunter.`,
    ],
    relatedTitle: 'Ähnliche Konvertierungen',
  },
  el: {
    eyebrow: 'Πρακτική μετατροπή',
    title: (from, to) => `Μετατροπή ${from} σε ${to} online`,
    lead: (from, to) =>
      `Ανεβάστε ένα αρχείο ${from}, προσαρμόστε τις διαθέσιμες επιλογές και κατεβάστε το αποτέλεσμα ${to} χωρίς υδατογράφημα.`,
    fileHandlingTitle: 'Χειρισμός αρχείων',
    fileHandling: (from, to) =>
      `Όταν το ${from} υποστηρίζεται από το πρόγραμμα περιήγησης, η μετατροπή σε ${to} γίνεται τοπικά. Τα server-only formats αποστέλλονται στο API μόνο για τη μετατροπή.`,
    stepsTitle: 'Πώς γίνεται η μετατροπή',
    steps: (from, to) => [
      `Ανεβάστε ένα αρχείο ${from} από τη συσκευή ή με URL.`,
      `Επιλέξτε προφίλ ${to}, ποιότητα ή συμπίεση όταν είναι διαθέσιμα.`,
      `Ξεκινήστε τη μετατροπή και κατεβάστε το αρχείο ${to}.`,
    ],
    relatedTitle: 'Σχετικές μετατροπές',
  },
  es: {
    eyebrow: 'Conversión práctica',
    title: (from, to) => `Convertir ${from} a ${to} online`,
    lead: (from, to) =>
      `Sube un archivo ${from}, ajusta las opciones disponibles y descarga el resultado ${to} sin marca de agua.`,
    fileHandlingTitle: 'Tratamiento de archivos',
    fileHandling: (from, to) =>
      `Cuando ${from} es compatible con el navegador, la conversión a ${to} se ejecuta localmente. Los formatos solo de servidor se envían al API únicamente durante la conversión.`,
    stepsTitle: 'Cómo convertir el archivo',
    steps: (from, to) => [
      `Sube un archivo ${from} desde tu dispositivo o por URL.`,
      `Elige el perfil ${to}, calidad o compresión cuando esté disponible.`,
      `Inicia la conversión y descarga el archivo ${to} final.`,
    ],
    relatedTitle: 'Conversiones relacionadas',
  },
  fr: {
    eyebrow: 'Conversion pratique',
    title: (from, to) => `Convertir ${from} en ${to} en ligne`,
    lead: (from, to) =>
      `Importez un fichier ${from}, ajustez les options disponibles et téléchargez le résultat ${to} sans filigrane.`,
    fileHandlingTitle: 'Traitement des fichiers',
    fileHandling: (from, to) =>
      `Lorsque ${from} est pris en charge par le navigateur, la conversion en ${to} s'exécute localement. Les formats côté serveur sont envoyés à l'API uniquement pour la conversion.`,
    stepsTitle: 'Comment convertir le fichier',
    steps: (from, to) => [
      `Importez un fichier ${from} depuis votre appareil ou par URL.`,
      `Choisissez le profil ${to}, la qualité ou la compression si disponible.`,
      `Lancez la conversion et téléchargez le fichier ${to} final.`,
    ],
    relatedTitle: 'Conversions associées',
  },
};

export function App() {
  const applicationSurface: ApplicationSurface = hostRoutingService.getApplicationSurface(window.location.hostname, window.location.pathname);

  useLayoutEffect(() => {
    document.documentElement.dataset.appSurface = applicationSurface;

    return () => {
      delete document.documentElement.dataset.appSurface;
    };
  }, [applicationSurface]);

  return applicationSurface === 'dashboard' ? <DashboardApp /> : <MainSiteApp />;
}

function DashboardApp() {
  const [language, setLanguage] = useState<LanguageCode>(() => getInitialLanguage());
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());
  const [accessState, setAccessState] = useState<DashboardAccessState>({
    ip: null,
    message: null,
    status: 'loading',
  });
  const [secretKey, setSecretKey] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmittingSecret, setIsSubmittingSecret] = useState(false);
  const t = useCallback((key: string) => translate(language, key), [language]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('kim-converter-language', language);
    localStorage.setItem('kim-converter-theme', theme);
    document.title = 'Dashboard - Kim Converter';
    setMetaByName('description', 'Kim Converter dashboard.');
    setMetaByName('robots', 'noindex, nofollow');
    setCanonicalLink(`${window.location.origin}/`);
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());

    const structuredData = document.getElementById('structured-data');
    if (structuredData) {
      structuredData.textContent = '';
    }
  }, [language, theme]);

  useEffect(() => {
    let isMounted = true;

    dashboardAccessService
      .getAccess()
      .then((access) => {
        if (!isMounted) {
          return;
        }

        if (!access.allowed) {
          setAccessState({ ip: access.ip, message: null, status: 'blocked' });
          return;
        }

        setAccessState({
          ip: access.ip,
          message: null,
          status: access.authenticated ? 'authenticated' : 'ready',
        });
      })
      .catch((caught: unknown) => {
        if (!isMounted) {
          return;
        }

        setAccessState({
          ip: null,
          message: getErrorMessage(caught),
          status: 'error',
        });
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSecretSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setAuthError(null);
      setIsSubmittingSecret(true);

      try {
        const access = await dashboardAccessService.authenticate(secretKey.trim());
        if (!access.allowed) {
          setAccessState({ ip: access.ip, message: null, status: 'blocked' });
          return;
        }

        setAccessState({ ip: access.ip, message: null, status: 'authenticated' });
      } catch (caught) {
        setAuthError(getErrorMessage(caught));
      } finally {
        setIsSubmittingSecret(false);
      }
    },
    [secretKey],
  );

  if (accessState.status === 'authenticated') {
    return (
      <div className="dashboard-shell">
        <DashboardHeader language={language} setLanguage={setLanguage} setTheme={setTheme} t={t} theme={theme} />
        <DashboardAnalyticsView />
      </div>
    );
  }

  return (
    <main aria-label="Kim Converter dashboard access" className="dashboard-auth-page">
      <DashboardAuthPanel
        authError={authError}
        isSubmitting={isSubmittingSecret}
        onSecretChange={setSecretKey}
        onSubmit={handleSecretSubmit}
        secretKey={secretKey}
        state={accessState}
      />
    </main>
  );
}

function DashboardHeader({
  language,
  setLanguage,
  setTheme,
  t,
  theme,
}: {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  setTheme: (theme: ThemeMode) => void;
  t: (key: string) => string;
  theme: ThemeMode;
}) {
  return (
    <header className="site-header dashboard-header">
      <a className="brand" href="/">
        <span className="brand__mark">K</span>
        <span>{t('appName')}</span>
      </a>
      <div className="header-controls">
        <LanguageSelector language={language} setLanguage={setLanguage} t={t} />
        <button
          className="icon-button"
          type="button"
          aria-label={theme === 'light' ? t('dark') : t('light')}
          onClick={() => {
            localStorage.setItem('kim-converter-theme-explicit', 'true');
            setTheme(theme === 'light' ? 'dark' : 'light');
          }}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </div>
    </header>
  );
}

function DashboardAuthPanel({
  authError,
  isSubmitting,
  onSecretChange,
  onSubmit,
  secretKey,
  state,
}: {
  authError: string | null;
  isSubmitting: boolean;
  onSecretChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  secretKey: string;
  state: DashboardAccessState;
}) {
  if (state.status === 'loading') {
    return (
      <section className="dashboard-auth-card" aria-live="polite">
        <Loader2 className="dashboard-auth-card__spinner" size={24} />
        <h1>Проверяем доступ</h1>
      </section>
    );
  }

  if (state.status === 'blocked') {
    return (
      <section className="dashboard-auth-card dashboard-auth-card--blocked">
        <ShieldCheck size={24} />
        <h1>Вас нет в белом списке</h1>
        <p>
          Ваш IP: <code>{state.ip}</code>
        </p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="dashboard-auth-card dashboard-auth-card--blocked">
        <X size={24} />
        <h1>Не удалось проверить доступ</h1>
        <p>{state.message}</p>
      </section>
    );
  }

  return (
    <section className="dashboard-auth-card">
      <ShieldCheck size={24} />
      <h1>Вход в dashboard</h1>
      <form className="dashboard-auth-form" onSubmit={onSubmit}>
        <label className="field-label">
          <span>Secret key</span>
          <input
            autoComplete="current-password"
            autoFocus
            onChange={(event) => onSecretChange(event.target.value)}
            placeholder="Введите secret key"
            type="password"
            value={secretKey}
          />
        </label>
        {authError ? <p className="dashboard-auth-form__error">{authError}</p> : null}
        <button className="primary-button full-width" disabled={isSubmitting || secretKey.trim().length === 0} type="submit">
          {isSubmitting ? <Loader2 className="dashboard-auth-card__spinner" size={17} /> : <KeyRound size={17} />}
          Войти
        </button>
      </form>
    </section>
  );
}

function DashboardAnalyticsView() {
  const [rangeDays, setRangeDays] = useState(14);
  const [summary, setSummary] = useState<DashboardAnalyticsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      setSummary(await dashboardAnalyticsService.getSummary(rangeDays));
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setIsLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadSummary();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [loadSummary]);

  const chartData = useMemo(
    () =>
      summary?.timeseries.map((point) => ({
        ...point,
        label: formatDashboardDate(point.date),
      })) ?? [],
    [summary],
  );

  return (
    <main aria-label="Kim Converter dashboard" className="dashboard-canvas dashboard-canvas--loaded">
      <section className="dashboard-page">
        <div className="dashboard-page__head">
          <div>
            <span className="eyebrow">
              <BarChart3 size={16} />
              Privacy-friendly analytics
            </span>
            <h1>Dashboard Kim Converter</h1>
            <p>Сводка по визитам и действиям без хранения IP, user-agent, имен файлов и импортируемых URL.</p>
          </div>
          <div className="dashboard-page__actions">
            <label className="field-label">
              Период
              <select value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}>
                <option value={7}>7 дней</option>
                <option value={14}>14 дней</option>
                <option value={30}>30 дней</option>
              </select>
            </label>
            <button className="secondary-button" type="button" onClick={loadSummary} disabled={isLoading}>
              <RefreshCw className={isLoading ? 'dashboard-spin-icon' : undefined} size={17} />
              Обновить
            </button>
          </div>
        </div>

        {error ? <p className="dashboard-alert">{error}</p> : null}
        {isLoading && !summary ? <DashboardLoadingState /> : null}
        {summary ? (
          <>
            <div className="dashboard-metrics">
              <DashboardMetricCard icon={<Users size={20} />} label="Визиты" value={summary.totals.visits} hint="site_visit за период" />
              <DashboardMetricCard icon={<Eye size={20} />} label="Просмотры" value={summary.totals.pageViews} hint="page_view без query/hash" />
              <DashboardMetricCard icon={<Activity size={20} />} label="События" value={summary.totals.events} hint="Все действия пользователей" />
              <DashboardMetricCard icon={<TrendingUp size={20} />} label="Конверсии" value={summary.totals.conversions} hint="Успешные запуски обработки" />
            </div>

            <div className="dashboard-grid">
              <DashboardPanel className="dashboard-panel--wide" title="Динамика по дням">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={310}>
                    <AreaChart data={chartData} margin={{ left: -18, right: 12, top: 10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="dashboardVisits" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.32} />
                          <stop offset="95%" stopColor="var(--accent)" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="dashboardConversions" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="5%" stopColor="var(--warm)" stopOpacity={0.32} />
                          <stop offset="95%" stopColor="var(--warm)" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="var(--line)" strokeDasharray="4 6" vertical={false} />
                      <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                      <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} allowDecimals={false} />
                      <Tooltip contentStyle={dashboardTooltipStyle} labelStyle={dashboardTooltipLabelStyle} />
                      <Area type="monotone" dataKey="visits" name="Визиты" stroke="var(--accent)" fill="url(#dashboardVisits)" strokeWidth={2.5} />
                      <Area type="monotone" dataKey="conversions" name="Конверсии" stroke="var(--warm)" fill="url(#dashboardConversions)" strokeWidth={2.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <DashboardEmptyState text="Данных за выбранный период пока нет." />
                )}
              </DashboardPanel>

              <DashboardPanel title="События">
                {summary.topEvents.length > 0 ? (
                  <ResponsiveContainer width="100%" height={310}>
                    <BarChart data={summary.topEvents.slice(0, 8)} layout="vertical" margin={{ left: 18, right: 12, top: 4, bottom: 4 }}>
                      <CartesianGrid stroke="var(--line)" strokeDasharray="4 6" horizontal={false} />
                      <XAxis type="number" hide />
                      <YAxis dataKey="name" type="category" width={132} tickFormatter={formatDashboardEventName} tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                      <Tooltip contentStyle={dashboardTooltipStyle} labelFormatter={formatDashboardTooltipLabel} labelStyle={dashboardTooltipLabelStyle} />
                      <Bar dataKey="count" name="События" fill="var(--accent)" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <DashboardEmptyState text="События появятся после согласия на аналитику." />
                )}
              </DashboardPanel>

              <DashboardPanel title="Популярные страницы">
                <DashboardPageList pages={summary.topPages} />
              </DashboardPanel>

              <DashboardPanel title="Форматы">
                {summary.formats.length > 0 ? (
                  <ResponsiveContainer width="100%" height={268}>
                    <PieChart>
                      <Pie data={summary.formats.slice(0, 6)} dataKey="count" nameKey="name" innerRadius={62} outerRadius={96} paddingAngle={3}>
                        {summary.formats.slice(0, 6).map((item, index) => (
                          <Cell key={item.name} fill={dashboardChartColors[index % dashboardChartColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={dashboardTooltipStyle} labelStyle={dashboardTooltipLabelStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <DashboardEmptyState text="Форматы появятся после обработок и загрузок." />
                )}
              </DashboardPanel>

              <DashboardPanel title="Профили конвертации">
                <DashboardProfileList profiles={summary.topProfiles} />
              </DashboardPanel>

              <DashboardPanel className="dashboard-panel--wide" title="Последние действия">
                <DashboardRecentEvents events={summary.recentEvents} />
              </DashboardPanel>
            </div>
          </>
        ) : null}
      </section>
    </main>
  );
}

function DashboardMetricCard({ icon, hint, label, value }: { icon: ReactNode; hint: string; label: string; value: number }) {
  return (
    <article className="dashboard-metric-card">
      <span>{icon}</span>
      <div>
        <p>{label}</p>
        <strong>{formatDashboardNumber(value)}</strong>
        <small>{hint}</small>
      </div>
    </article>
  );
}

function DashboardPanel({ children, className = '', title }: { children: ReactNode; className?: string; title: string }) {
  return (
    <section className={`dashboard-panel ${className}`}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function DashboardPageList({ pages }: { pages: DashboardAnalyticsSummary['topPages'] }) {
  if (pages.length === 0) {
    return <DashboardEmptyState text="Просмотры страниц пока не записаны." />;
  }

  return (
    <div className="dashboard-list">
      {pages.slice(0, 8).map((page) => (
        <div className="dashboard-list__row" key={page.path}>
          <div>
            <strong title={page.title}>{page.title || page.path}</strong>
            <span>{page.path}</span>
          </div>
          <b>{formatDashboardNumber(page.count)}</b>
        </div>
      ))}
    </div>
  );
}

function DashboardProfileList({ profiles }: { profiles: DashboardAnalyticsSummary['topProfiles'] }) {
  if (profiles.length === 0) {
    return <DashboardEmptyState text="Профили появятся после конвертации файлов." />;
  }

  return (
    <div className="dashboard-list">
      {profiles.slice(0, 8).map((profile) => (
        <div className="dashboard-list__row" key={profile.profileId}>
          <div>
            <strong>{profile.profileId}</strong>
            <span>Успешно: {formatDashboardNumber(profile.successCount)} · скачивания: {formatDashboardNumber(profile.downloads)}</span>
          </div>
          <b>{formatDashboardNumber(profile.conversions)}</b>
        </div>
      ))}
    </div>
  );
}

function DashboardRecentEvents({ events }: { events: DashboardAnalyticsSummary['recentEvents'] }) {
  if (events.length === 0) {
    return <DashboardEmptyState text="Последние действия появятся после первых событий." />;
  }

  return (
    <div className="dashboard-events">
      {events.map((event) => (
        <article className="dashboard-event" key={event.id}>
          <span>{formatDashboardEventName(event.name)}</span>
          <strong>{event.path}</strong>
          <small>{formatDashboardDateTime(event.timestamp)}</small>
        </article>
      ))}
    </div>
  );
}

function DashboardLoadingState() {
  return (
    <div className="dashboard-loading">
      <Loader2 className="dashboard-auth-card__spinner" size={22} />
      <span>Загружаем аналитику</span>
    </div>
  );
}

function DashboardEmptyState({ text }: { text: string }) {
  return (
    <div className="dashboard-empty">
      <Activity size={18} />
      <span>{text}</span>
    </div>
  );
}

const dashboardChartColors = ['#2563eb', '#f97316', '#16805b', '#7c3aed', '#0891b2', '#c24135'];
const dashboardTooltipStyle: CSSProperties = {
  background: 'var(--bg-strong)',
  border: '1px solid var(--line)',
  borderRadius: 8,
  boxShadow: 'var(--shadow)',
  color: 'var(--text)',
};
const dashboardTooltipLabelStyle: CSSProperties = {
  color: 'var(--muted)',
  fontWeight: 800,
};

function formatDashboardNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

function formatDashboardDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' }).format(new Date(`${value}T00:00:00`));
}

function formatDashboardDateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
  }).format(new Date(value));
}

function formatDashboardEventName(name: string): string {
  const labels: Record<string, string> = {
    conversion_profile_select: 'выбор формата',
    file_convert: 'конвертация',
    file_download: 'скачивание файла',
    file_download_all: 'скачивание ZIP',
    file_upload: 'загрузка файлов',
    image_download: 'скачивание изображения',
    image_process: 'обработка изображения',
    image_upload: 'загрузка изображения',
    page_view: 'просмотр страницы',
    site_visit: 'визит',
  };

  return labels[name] ?? name.replace(/_/g, ' ');
}

function formatDashboardTooltipLabel(label: ReactNode): string {
  return formatDashboardEventName(String(label ?? ''));
}

function MainSiteApp() {
  const [language, setLanguage] = useState<LanguageCode>(() => getInitialLanguage());
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());
  const [route, setRoute] = useState<AppRoute>(() => getInitialRoute());
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [consentPreferences, setConsentPreferences] = useState<ConsentPreferences | null>(() =>
    consentService.getPreferences(),
  );
  const t = useCallback((key: string) => translate(language, key), [language]);
  const conversionPair = route === 'files' ? getConversionPairFromPath(getRoutePathWithoutLanguage(window.location.pathname)) : null;

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('kim-converter-language', language);
    localStorage.setItem('kim-converter-theme', theme);
    updateDocumentSeo(route, language, conversionPair);

    const nextPath = getLocalizedPath(route, language, conversionPair);
    if (window.location.pathname !== nextPath) {
      window.history.replaceState({}, '', nextPath);
    }
  }, [conversionPair, language, route, theme]);

  useEffect(() => {
    const syncLocation = () => {
      setRoute(getInitialRoute());
      setLanguage(getPathLanguage(window.location.pathname) ?? 'ru');
    };
    window.addEventListener('popstate', syncLocation);
    window.addEventListener('hashchange', syncLocation);

    return () => {
      window.removeEventListener('popstate', syncLocation);
      window.removeEventListener('hashchange', syncLocation);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [route]);

  useEffect(() => {
    yandexMetrikaService.sync(consentPreferences, window.location.href, document.title);
    kimAnalyticsService.sync(consentPreferences);
    kimAnalyticsService.trackPageView({
      language,
      path: window.location.pathname,
      route,
      title: document.title,
    });
  }, [consentPreferences, language, route]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const animatedElements = Array.from(
      document.querySelectorAll<HTMLElement>(
        [
          '.hero',
          '.task-choice',
          '.tool-info',
          '.benefits-area',
          '.seo-content',
          '.legal-page__head',
          '.workspace-page__head',
          '.workspace',
          '.home-upload',
          '.task-card',
          '.benefit-card',
          '.legal-grid article',
          '.converter-upload-stage',
          '.converter-start-panel',
          '.file-queue',
          '.conversion-sidebar',
          '.upload-panel',
          '.editor-panel',
          '.settings-panel',
        ].join(', '),
      ),
    );

    animatedElements.forEach((element, index) => {
      element.classList.add('reveal-on-scroll');
      element.style.setProperty('--reveal-delay', isStaggeredRevealElement(element) ? `${Math.min(index % 4, 3) * 35}ms` : '0ms');

      if (revealedElements.has(element) || isElementInRevealViewport(element)) {
        revealedElements.add(element);
        element.classList.add('is-visible');
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            revealedElements.add(element);
            element.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px 14% 0px', threshold: 0.01 },
    );

    animatedElements
      .filter((element) => !revealedElements.has(element))
      .forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [route]);

  const navigateTo = useCallback(
    (nextRoute: AppRoute) => {
      setRoute(nextRoute);
      window.history.pushState({}, '', getLocalizedPath(nextRoute, language));
    },
    [language],
  );

  const startFromFiles = useCallback(
    (files: File[], targetRoute?: 'images' | 'files') => {
      if (files.length === 0) {
        return;
      }

      const nextRoute: AppRoute = targetRoute ?? (files.length === 1 && isPreviewableImage(files[0]) ? 'images' : 'files');
      setPendingUpload({
        id: `${Date.now()}-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`,
        files,
      });
      navigateTo(nextRoute);
    },
    [navigateTo],
  );

  const clearPendingUpload = useCallback(() => {
    setPendingUpload(null);
  }, []);

  const startImageEditing = useCallback((files: File[]) => startFromFiles(files, 'images'), [startFromFiles]);
  const startFileConversion = useCallback((files: File[]) => startFromFiles(files, 'files'), [startFromFiles]);

  const scrollHomeSection = useCallback(
    (sectionId: string) => {
      if (route !== 'home') {
        navigateTo('home');
      }

      window.setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    },
    [navigateTo, route],
  );

  return (
    <div className="app-shell">
      <Header
        language={language}
        navigateTo={navigateTo}
        scrollHomeSection={scrollHomeSection}
        setLanguage={setLanguage}
        setTheme={setTheme}
        theme={theme}
        t={t}
      />
      <main>
        {route === 'home' ? (
          <HomePage
            language={language}
            onStartFileConversion={startFileConversion}
            onStartImageEditing={startImageEditing}
            scrollHomeSection={scrollHomeSection}
            t={t}
          />
        ) : null}
        {route === 'fileInfo' ? (
          <ToolInfoPage
            kind="files"
            language={language}
            navigateTo={navigateTo}
            onStartFromFiles={startFileConversion}
            t={t}
          />
        ) : null}
        {route === 'imageInfo' ? (
          <ToolInfoPage
            kind="images"
            language={language}
            navigateTo={navigateTo}
            onStartFromFiles={startImageEditing}
            t={t}
          />
        ) : null}
        {route === 'images' ? <WorkspacePage initialUpload={pendingUpload} kind="images" language={language} navigateTo={navigateTo} onUploadConsumed={clearPendingUpload} t={t} /> : null}
        {route === 'files' ? (
          <WorkspacePage
            conversionPair={conversionPair}
            initialUpload={pendingUpload}
            kind="files"
            language={language}
            navigateTo={navigateTo}
            onUploadConsumed={clearPendingUpload}
            t={t}
          />
        ) : null}
        {route === 'privacy' ? <PrivacyPage consentPreferences={consentPreferences} setConsentPreferences={setConsentPreferences} t={t} /> : null}
      </main>
      <Footer language={language} navigateTo={navigateTo} scrollHomeSection={scrollHomeSection} t={t} />
      <ConsentBanner
        preferences={consentPreferences}
        setPreferences={setConsentPreferences}
        t={t}
      />
    </div>
  );
}

interface HeaderProps {
  language: LanguageCode;
  navigateTo: (route: AppRoute) => void;
  scrollHomeSection: (sectionId: string) => void;
  setLanguage: (language: LanguageCode) => void;
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  t: (key: string) => string;
}

function Header({ language, navigateTo, scrollHomeSection, setLanguage, theme, setTheme, t }: HeaderProps) {
  return (
    <header className="site-header">
      <a className="brand" href={getLocalizedPath('home', language)} onClick={(event) => handleRouteLinkClick(event, 'home', navigateTo)}>
        <span className="brand__mark">K</span>
        <span>{t('appName')}</span>
      </a>
      <nav className="site-nav" aria-label="Primary">
        <ToolsMenu language={language} navigateTo={navigateTo} t={t} />
        <a href={`${getLocalizedPath('home', language)}#benefits`} onClick={(event) => handleSectionLinkClick(event, 'benefits', scrollHomeSection)}>
          {t('navSeo')}
        </a>
      </nav>
      <div className="header-controls">
        <LanguageSelector language={language} setLanguage={setLanguage} t={t} />
        <button
          className="icon-button"
          type="button"
          aria-label={theme === 'light' ? t('dark') : t('light')}
          onClick={() => {
            localStorage.setItem('kim-converter-theme-explicit', 'true');
            setTheme(theme === 'light' ? 'dark' : 'light');
          }}
        >
          {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </div>
    </header>
  );
}

function ToolsMenu({
  language,
  navigateTo,
  t,
}: {
  language: LanguageCode;
  navigateTo: (route: AppRoute) => void;
  t: (key: string) => string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);

    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen]);

  const openRoute = (event: MouseEvent<HTMLAnchorElement>, route: AppRoute) => {
    const useNativeNavigation = shouldUseNativeNavigation(event);
    handleRouteLinkClick(event, route, navigateTo);
    if (!useNativeNavigation) {
      setIsOpen(false);
    }
  };

  return (
    <div className="tools-menu" ref={containerRef}>
      <button
        className="tools-menu__trigger"
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => setIsOpen((current) => !current)}
      >
        {t('navTools')}
        <ChevronDown size={15} />
      </button>
      {isOpen ? (
        <div className="tools-menu__panel" role="menu">
          <a href={getLocalizedPath('fileInfo', language)} role="menuitem" onClick={(event) => openRoute(event, 'fileInfo')}>
            <Files size={17} />
            {t('fileCategoryTitle')}
          </a>
          <a href={getLocalizedPath('imageInfo', language)} role="menuitem" onClick={(event) => openRoute(event, 'imageInfo')}>
            <FileImage size={17} />
            {t('imageCategoryTitle')}
          </a>
        </div>
      ) : null}
    </div>
  );
}

function HomePage({
  language,
  onStartFileConversion,
  onStartImageEditing,
  scrollHomeSection,
  t,
}: {
  language: LanguageCode;
  onStartFileConversion: (files: File[]) => void;
  onStartImageEditing: (files: File[]) => void;
  scrollHomeSection: (sectionId: string) => void;
  t: (key: string) => string;
}) {
  return (
    <>
      <section className="hero">
        <div className="hero__content">
          <span className="eyebrow">
            <Sparkles size={16} />
            {t('heroPrivacy')}
          </span>
          <h1>{t('heroTitle')}</h1>
          <p>{t('heroText')}</p>
          <ConversionFlowAnimation language={language} t={t} />
          <div className="hero__actions">
            <a
              className="primary-button primary-button--large"
              href={`${getLocalizedPath('home', language)}#categories`}
              onClick={(event) => handleSectionLinkClick(event, 'categories', scrollHomeSection)}
            >
              <UploadCloud size={18} />
              {t('startWork')}
            </a>
            <a
              className="secondary-button"
              href={`${getLocalizedPath('home', language)}#benefits`}
              onClick={(event) => handleSectionLinkClick(event, 'benefits', scrollHomeSection)}
            >
              <BadgeCheck size={18} />
              {t('heroSecondaryCta')}
            </a>
          </div>
        </div>
      </section>

      <section className="task-choice" id="categories" aria-labelledby="task-choice-title">
        <div className="section-heading">
          <span>{t('taskChoiceEyebrow')}</span>
          <h2 id="task-choice-title">{t('taskChoiceTitle')}</h2>
          <p>{t('taskChoiceText')}</p>
        </div>

        <div className="task-choice__grid">
          <TaskChoiceCard
            icon={<Files size={24} />}
            badge={t('fileTaskBadge')}
            title={t('fileCategoryTitle')}
            text={t('fileCategoryText')}
          >
            <HomeUploadLauncher
              compact
              title={t('homeConverterUploadTitle')}
              text={t('homeConverterUploadText')}
              onStartFromFiles={onStartFileConversion}
              t={t}
            />
          </TaskChoiceCard>
          <TaskChoiceCard
            icon={<FileImage size={24} />}
            badge={t('imageTaskBadge')}
            title={t('imageCategoryTitle')}
            text={t('imageCategoryText')}
          >
            <HomeUploadLauncher
              accept="image/png,image/jpeg,image/webp,image/avif,image/bmp,image/gif,image/svg+xml"
              compact
              fileFilter={isPreviewableImage}
              multiple={false}
              title={t('homeImageUploadTitle')}
              text={t('homeImageUploadText')}
              onStartFromFiles={onStartImageEditing}
              t={t}
            />
          </TaskChoiceCard>
        </div>
      </section>

      <section className="benefits-area" id="benefits">
        <div className="section-heading">
          <span>{t('navSeo')}</span>
          <h2>{t('seoTitle')}</h2>
          <p>{t('seoText')}</p>
        </div>

        <div className="benefit-grid">
          <BenefitCard icon={<BadgeCheck size={22} />} title={t('freeTitle')} text={t('freeText')} />
          <BenefitCard icon={<ShieldCheck size={22} />} title={t('watermarkTitle')} text={t('watermarkText')} />
          <BenefitCard icon={<Sparkles size={22} />} title={t('unlimitedTitle')} text={t('unlimitedText')} />
        </div>
      </section>

      <section className="seo-content" id="formats">
        <div>
          <span>{t('seoFormatsEyebrow')}</span>
          <h2>{t('seoFormatsTitle')}</h2>
          <p>{t('seoFormatsText')}</p>
        </div>
        <ul>
          <li>{t('seoFormatsImage')}</li>
          <li>{t('seoFormatsFiles')}</li>
          <li>{t('seoFormatsPrivacy')}</li>
        </ul>
      </section>

      <PopularConvertersSection language={language} t={t} />
      <SeoFaqSection items={getSeoFaqItems('home', language)} title={getSeoFaqTitle(language)} />
    </>
  );
}

function PopularConvertersSection({ language, t }: { language: LanguageCode; t: (key: string) => string }) {
  return (
    <section className="popular-converters" aria-labelledby="popular-converters-title">
      <div className="section-heading">
        <span>{t('popularConvertersEyebrow')}</span>
        <h2 id="popular-converters-title">{t('popularConvertersTitle')}</h2>
        <p>{t('popularConvertersText')}</p>
      </div>

      <div className="popular-converters__grid">
        {popularConversionLinks.map((link) => (
          <a key={link.slug} href={getLocalizedConversionPath(link.slug, language)}>
            <span>{link.label}</span>
            <p>{link.description[language]}</p>
          </a>
        ))}
      </div>
    </section>
  );
}

function SeoFaqSection({ items, title }: { items: SeoFaqItem[]; title: string }) {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="seo-faq" aria-labelledby="seo-faq-title">
      <div className="section-heading">
        <span>FAQ</span>
        <h2 id="seo-faq-title">{title}</h2>
      </div>
      <div className="seo-faq__grid">
        {items.map((item) => (
          <article key={item.question}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function TaskChoiceCard({
  badge,
  children,
  icon,
  text,
  title,
}: {
  badge: string;
  children: ReactNode;
  icon: ReactNode;
  text: string;
  title: string;
}) {
  return (
    <article className="task-card">
      <div className="task-card__head">
        <span className="task-card__icon">{icon}</span>
        <div>
          <span className="task-card__badge">{badge}</span>
          <h3>{title}</h3>
          <p>{text}</p>
        </div>
      </div>
      {children}
    </article>
  );
}

function ConversionFlowAnimation({ language, t }: { language: LanguageCode; t: (key: string) => string }) {
  const conversionPairs = [
    { input: 'PNG', output: 'JPG' },
    { input: 'WEBP', output: 'PNG' },
    { input: 'MP3', output: 'WAV' },
    { input: 'MP4', output: 'WEBM' },
    { input: 'JSON', output: 'CSV' },
    { input: 'TXT', output: 'PDF' },
  ];
  const [activePairIndex, setActivePairIndex] = useState(0);
  const [manualPair, setManualPair] = useState<ConversionPair | null>(null);
  const activePair = conversionPairs[activePairIndex];
  const activeConversionPair = manualPair ?? {
    from: activePair.input.toLowerCase(),
    to: activePair.output.toLowerCase(),
  };

  useEffect(() => {
    if (manualPair) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActivePairIndex((current) => (current + 1) % conversionPairs.length);
    }, 2600);

    return () => window.clearInterval(intervalId);
  }, [conversionPairs.length, manualPair]);

  return (
    <div className="conversion-flow">
      <div
        className="conversion-flow__card conversion-flow__card--source"
      >
        <span>{t('conversionInput')}</span>
        <FormatPicker
          formats={getConversionFlowInputFormats()}
          t={t}
          value={activeConversionPair.from}
          onChange={(format) => setManualPair((current) => ({ from: format, to: current?.to ?? activeConversionPair.to }))}
        />
      </div>
      <div className="conversion-flow__track">
        <span className="conversion-flow__particle conversion-flow__particle--one" />
        <span className="conversion-flow__particle conversion-flow__particle--two" />
        <a
          className="conversion-flow__action"
          href={getLocalizedPath('files', language, activeConversionPair)}
          aria-label={t('conversionOpen')}
        >
          <ArrowRight size={22} />
        </a>
      </div>
      <div
        className="conversion-flow__card conversion-flow__card--target"
      >
        <span>{t('conversionOutput')}</span>
        <FormatPicker
          formats={getConversionFlowOutputFormats(activeConversionPair.from)}
          t={t}
          value={activeConversionPair.to}
          onChange={(format) => setManualPair((current) => ({ from: current?.from ?? activeConversionPair.from, to: format }))}
        />
      </div>
    </div>
  );
}

function HomeUploadLauncher({
  accept,
  compact = false,
  fileFilter,
  multiple = true,
  onStartFromFiles,
  text,
  title,
  t,
}: {
  accept?: string;
  compact?: boolean;
  fileFilter?: (file: File) => boolean;
  multiple?: boolean;
  onStartFromFiles: (files: File[]) => void;
  text?: string;
  title?: string;
  t: (key: string) => string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [urlValue, setUrlValue] = useState('');
  const [isUrlLoading, setIsUrlLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startFiles = (files: File[]) => {
    const validFiles = files.filter((file) => file.size > 0 && (!fileFilter || fileFilter(file)));
    if (validFiles.length === 0) {
      return;
    }

    setError(null);
    onStartFromFiles(multiple ? validFiles : validFiles.slice(0, 1));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    startFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    startFiles(Array.from(event.dataTransfer.files));
  };

  const handleUrlSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedUrl = urlValue.trim();
    if (!trimmedUrl) {
      return;
    }

    setIsUrlLoading(true);
    setError(null);

    try {
      startFiles([await fetchRemoteFile(trimmedUrl)]);
    } catch {
      setError(t('homeUploadUrlError'));
    } finally {
      setIsUrlLoading(false);
    }
  };

  return (
    <div className={compact ? 'home-upload home-upload--compact' : 'home-upload'}>
      <label
        className="home-upload__dropzone"
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <input accept={accept} ref={inputRef} multiple={multiple} type="file" onChange={handleInputChange} />
        <span className="home-upload__icon">
          <UploadCloud size={34} />
        </span>
        <strong>{title ?? t('homeUploadTitle')}</strong>
        <span>{text ?? t('homeUploadText')}</span>
        <button
          className="primary-button"
          type="button"
          onClick={(event) => {
            event.preventDefault();
            inputRef.current?.click();
          }}
        >
          <UploadCloud size={17} />
          {t('chooseFile')}
        </button>
      </label>

      <form className="home-upload__url" onSubmit={handleUrlSubmit}>
        <label className="home-upload__url-field">
          <Link2 size={17} />
          <input
            type="url"
            value={urlValue}
            placeholder={t('homeUploadUrlPlaceholder')}
            onChange={(event) => setUrlValue(event.target.value)}
          />
        </label>
        <button className="secondary-button" type="submit" disabled={isUrlLoading || !urlValue.trim()}>
          <ArrowRight size={17} />
          {isUrlLoading ? '...' : t('homeUploadUrlButton')}
        </button>
        {error ? <p className="error-text">{error}</p> : null}
      </form>
    </div>
  );
}

function Footer({
  language,
  navigateTo,
  scrollHomeSection,
  t,
}: {
  language: LanguageCode;
  navigateTo: (route: AppRoute) => void;
  scrollHomeSection: (sectionId: string) => void;
  t: (key: string) => string;
}) {
  return (
    <footer className="site-footer">
      <div>
        <div className="brand site-footer__brand">
          <span className="brand__mark">K</span>
          <span>{t('appName')}</span>
        </div>
        <p>{t('footerText')}</p>
      </div>

      <nav className="site-footer__nav" aria-label="Footer">
        <a href={`${getLocalizedPath('home', language)}#categories`} onClick={(event) => handleSectionLinkClick(event, 'categories', scrollHomeSection)}>
          {t('navTools')}
        </a>
        <a href={getLocalizedPath('images', language)} onClick={(event) => handleRouteLinkClick(event, 'images', navigateTo)}>
          {t('imageCategoryTitle')}
        </a>
        <a href={getLocalizedPath('files', language)} onClick={(event) => handleRouteLinkClick(event, 'files', navigateTo)}>
          {t('fileCategoryTitle')}
        </a>
        <a href={`${getLocalizedPath('home', language)}#benefits`} onClick={(event) => handleSectionLinkClick(event, 'benefits', scrollHomeSection)}>
          {t('navSeo')}
        </a>
        <a href={getLocalizedPath('privacy', language)} onClick={(event) => handleRouteLinkClick(event, 'privacy', navigateTo)}>
          {t('navPrivacy')}
        </a>
      </nav>
    </footer>
  );
}

function ToolInfoPage({
  kind,
  language,
  navigateTo,
  onStartFromFiles,
  t,
}: {
  kind: 'images' | 'files';
  language: LanguageCode;
  navigateTo: (route: AppRoute) => void;
  onStartFromFiles: (files: File[]) => void;
  t: (key: string) => string;
}) {
  const isImages = kind === 'images';
  const workspaceRoute: AppRoute = isImages ? 'images' : 'files';

  return (
    <>
      <section className="tool-info">
        <div className="tool-info__content">
          <span className="eyebrow">
            {isImages ? <FileImage size={16} /> : <Files size={16} />}
            {isImages ? t('imageTaskBadge') : t('fileTaskBadge')}
          </span>
          <h1>{isImages ? t('imageInfoTitle') : t('fileInfoTitle')}</h1>
          <p>{isImages ? t('imageInfoText') : t('fileInfoText')}</p>
          <div className="hero__actions">
            <a className="primary-button primary-button--large" href={getLocalizedPath(workspaceRoute, language)} onClick={(event) => handleRouteLinkClick(event, workspaceRoute, navigateTo)}>
              <UploadCloud size={18} />
              {isImages ? t('imageInfoCta') : t('fileInfoCta')}
            </a>
          </div>
        </div>
        <HomeUploadLauncher
          accept={isImages ? 'image/png,image/jpeg,image/webp,image/avif,image/bmp,image/gif,image/svg+xml' : undefined}
          compact
          fileFilter={isImages ? isPreviewableImage : undefined}
          multiple={!isImages}
          title={isImages ? t('homeImageUploadTitle') : t('homeConverterUploadTitle')}
          text={isImages ? t('homeImageUploadText') : t('homeConverterUploadText')}
          onStartFromFiles={onStartFromFiles}
          t={t}
        />
      </section>

      <section className="seo-content">
        <div>
          <span>{isImages ? t('imageInfoDetailsEyebrow') : t('fileInfoDetailsEyebrow')}</span>
          <h2>{isImages ? t('imageInfoDetailsTitle') : t('fileInfoDetailsTitle')}</h2>
          <p>{isImages ? t('imageInfoDetailsText') : t('fileInfoDetailsText')}</p>
        </div>
        <ul>
          <li>{isImages ? t('imageInfoPointOne') : t('fileInfoPointOne')}</li>
          <li>{isImages ? t('imageInfoPointTwo') : t('fileInfoPointTwo')}</li>
          <li>{isImages ? t('imageInfoPointThree') : t('fileInfoPointThree')}</li>
        </ul>
      </section>

      <section className="benefits-area">
        <div className="benefit-grid">
          <BenefitCard icon={<BadgeCheck size={22} />} title={t('freeTitle')} text={t('freeText')} />
          <BenefitCard icon={<ShieldCheck size={22} />} title={t('watermarkTitle')} text={t('watermarkText')} />
          <BenefitCard icon={<Sparkles size={22} />} title={t('unlimitedTitle')} text={t('unlimitedText')} />
        </div>
      </section>
    </>
  );
}

function WorkspacePage({
  conversionPair,
  initialUpload,
  kind,
  language,
  navigateTo,
  onUploadConsumed,
  t,
}: {
  conversionPair?: ConversionPair | null;
  initialUpload: PendingUpload | null;
  kind: 'images' | 'files';
  language: LanguageCode;
  navigateTo: (route: AppRoute) => void;
  onUploadConsumed: () => void;
  t: (key: string) => string;
}) {
  const isImages = kind === 'images';
  const pairTitle = conversionPair ? getConversionPairTitle(conversionPair, language) : null;

  return (
    <section className={isImages ? 'workspace-page' : 'workspace-page workspace-page--files'}>
      <div className="workspace-page__head">
        <a className="secondary-button" href={getLocalizedPath('home', language)} onClick={(event) => handleRouteLinkClick(event, 'home', navigateTo)}>
          <ArrowLeft size={17} />
          {t('backToCategories')}
        </a>
        <div>
          <span className="eyebrow">
            {isImages ? <FileImage size={16} /> : <Files size={16} />}
            {isImages ? t('imageCategoryTitle') : t('fileCategoryTitle')}
          </span>
          <h1>{pairTitle ?? (isImages ? t('imagePageTitle') : t('filePageTitle'))}</h1>
          <p>{conversionPair ? getConversionPairDescription(conversionPair, language) : isImages ? t('imagePageText') : t('filePageText')}</p>
        </div>
      </div>

      {isImages ? (
        <ImageWorkspace initialUpload={initialUpload} onUploadConsumed={onUploadConsumed} t={t} />
      ) : (
        <FileWorkspace conversionPair={conversionPair ?? null} initialUpload={initialUpload} onUploadConsumed={onUploadConsumed} t={t} />
      )}

      {conversionPair ? <ConversionSeoSection pair={conversionPair} language={language} /> : null}
      <SeoFaqSection
        items={getSeoFaqItems(isImages ? 'images' : 'files', language, conversionPair)}
        title={getSeoFaqTitle(language)}
      />
    </section>
  );
}

function ConversionSeoSection({ language, pair }: { language: LanguageCode; pair: ConversionPair }) {
  const copy = conversionSeoCopy[language];
  const from = pair.from.toUpperCase();
  const to = pair.to.toUpperCase();
  const relatedPairs = getRelatedConversionLinks(pair).slice(0, 8);

  return (
    <section className="conversion-seo" aria-labelledby="conversion-seo-title">
      <div className="conversion-seo__intro">
        <span>{copy.eyebrow}</span>
        <h2 id="conversion-seo-title">{copy.title(from, to)}</h2>
        <p>{copy.lead(from, to)}</p>
      </div>

      <div className="conversion-seo__panel">
        <h3>{copy.fileHandlingTitle}</h3>
        <p>{copy.fileHandling(from, to)}</p>
      </div>

      <div className="conversion-seo__panel">
        <h3>{copy.stepsTitle}</h3>
        <ol>
          {copy.steps(from, to).map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>

      {relatedPairs.length > 0 ? (
        <div className="conversion-seo__panel">
          <h3>{copy.relatedTitle}</h3>
          <div className="related-conversions">
            {relatedPairs.map((relatedPair) => (
              <a key={getConversionPairSlug(relatedPair)} href={getLocalizedConversionPath(getConversionPairSlug(relatedPair), language)}>
                {relatedPair.from.toUpperCase()} to {relatedPair.to.toUpperCase()}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function PrivacyPage({
  consentPreferences,
  setConsentPreferences,
  t,
}: {
  consentPreferences: ConsentPreferences | null;
  setConsentPreferences: (preferences: ConsentPreferences) => void;
  t: (key: string) => string;
}) {
  return (
    <section className="legal-page">
      <div className="legal-page__head">
        <span className="eyebrow">
          <ShieldCheck size={16} />
          {t('privacyEyebrow')}
        </span>
        <h1>{t('privacyTitle')}</h1>
        <p>{t('privacyIntro')}</p>
      </div>

      <div className="legal-grid">
        <article>
          <h2>{t('privacyFilesTitle')}</h2>
          <p>{t('privacyFilesText')}</p>
        </article>
        <article>
          <h2>{t('privacyStorageTitle')}</h2>
          <p>{t('privacyStorageText')}</p>
        </article>
        <article>
          <h2>{t('privacyAdsTitle')}</h2>
          <p>{t('privacyAdsText')}</p>
        </article>
        <article>
          <h2>{t('privacyControlTitle')}</h2>
          <p>{t('privacyControlText')}</p>
        </article>
      </div>

      <ConsentSettingsPanel
        preferences={consentPreferences}
        saveNotice={t('consentSaveNotice')}
        setPreferences={setConsentPreferences}
        t={t}
      />
    </section>
  );
}

function ConsentBanner({
  preferences,
  setPreferences,
  t,
}: {
  preferences: ConsentPreferences | null;
  setPreferences: (preferences: ConsentPreferences) => void;
  t: (key: string) => string;
}) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  if (preferences) {
    return null;
  }

  return (
    <div className="consent-banner" role="dialog" aria-live="polite" aria-label={t('consentTitle')}>
      <div className="consent-banner__text">
        <span>
          <Cookie size={18} />
          {t('consentTitle')}
        </span>
        <p>{t('consentText')}</p>
      </div>

      {isSettingsOpen ? (
        <ConsentSettingsPanel
          onBack={() => setIsSettingsOpen(false)}
          preferences={preferences}
          setPreferences={setPreferences}
          t={t}
        />
      ) : (
        <div className="consent-banner__actions">
          <button
            className="primary-button"
            type="button"
            onClick={() => setPreferences(consentService.acceptAll())}
          >
            <CheckCircle2 size={17} />
            {t('consentAccept')}
          </button>
          <button className="consent-banner__text-button" type="button" onClick={() => setIsSettingsOpen(true)}>
            {t('consentSettings')}
          </button>
        </div>
      )}
    </div>
  );
}

function ConsentSettingsPanel({
  onBack,
  preferences,
  saveNotice,
  setPreferences,
  t,
}: {
  onBack?: () => void;
  preferences: ConsentPreferences | null;
  saveNotice?: string;
  setPreferences: (preferences: ConsentPreferences) => void;
  t: (key: string) => string;
}) {
  const [analytics, setAnalytics] = useState(preferences?.analytics ?? false);
  const [advertising, setAdvertising] = useState(preferences?.advertising ?? false);
  const [isSaveNoticeVisible, setIsSaveNoticeVisible] = useState(false);

  const handleSavePreferences = () => {
    setPreferences(consentService.saveOptional({ analytics, advertising }));
    setIsSaveNoticeVisible(Boolean(saveNotice));
  };

  const handleAnalyticsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAnalytics(event.target.checked);
    setIsSaveNoticeVisible(false);
  };

  const handleAdvertisingChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAdvertising(event.target.checked);
    setIsSaveNoticeVisible(false);
  };

  return (
    <div className="consent-settings">
      {onBack ? (
        <button
          className="consent-settings__back"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={17} />
          {t('consentBack')}
        </button>
      ) : null}
      <label className="switch-row">
        <input type="checkbox" checked disabled />
        <span>
          <strong>{t('consentNecessaryTitle')}</strong>
          <small>{t('consentNecessaryText')}</small>
        </span>
      </label>
      <label className="switch-row">
        <input type="checkbox" checked={analytics} onChange={handleAnalyticsChange} />
        <span>
          <strong>{t('consentAnalyticsTitle')}</strong>
          <small>{t('consentAnalyticsText')}</small>
        </span>
      </label>
      <label className="switch-row">
        <input type="checkbox" checked={advertising} onChange={handleAdvertisingChange} />
        <span>
          <strong>{t('consentAdvertisingTitle')}</strong>
          <small>{t('consentAdvertisingText')}</small>
        </span>
      </label>
      <button
        className="primary-button full-width"
        type="button"
        onClick={handleSavePreferences}
      >
        <CheckCircle2 size={17} />
        {t('consentSave')}
      </button>
      {isSaveNoticeVisible && saveNotice ? (
        <p className="consent-settings__notice" role="status">
          {saveNotice}
        </p>
      ) : null}
    </div>
  );
}

function BenefitCard({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <article className="benefit-card">
      <span>{icon}</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function LanguageSelector({
  language,
  setLanguage,
  t,
}: {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeLanguage = languages.find((item) => item.code === language) ?? languages[0];

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);

    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen]);

  return (
    <div className="language-menu" ref={containerRef}>
      <button
        className="language-menu__trigger"
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen((current) => !current)}
      >
        <Languages size={16} />
        <span>{activeLanguage.nativeName}</span>
      </button>
      {isOpen && (
        <div className="language-menu__panel" role="listbox" aria-label={t('language')}>
          {languages.map((item) => (
            <button
              className={item.code === language ? 'is-active' : ''}
              key={item.code}
              type="button"
              role="option"
              aria-selected={item.code === language}
              onClick={() => {
                setLanguage(item.code);
                setIsOpen(false);
              }}
            >
              <span>{item.nativeName}</span>
              <small>{item.label}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageWorkspace({
  initialUpload,
  onUploadConsumed,
  t,
}: {
  initialUpload: PendingUpload | null;
  onUploadConsumed: () => void;
  t: (key: string) => string;
}) {
  const imageElement = useRef<HTMLImageElement | null>(null);
  const sourceUrlRef = useRef<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const consumedUploadIdRef = useRef<string | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [cropPreset, setCropPreset] = useState<CropPreset>('free');
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [resizeWidth, setResizeWidth] = useState<number | ''>('');
  const [resizeHeight, setResizeHeight] = useState<number | ''>('');
  const [keepRatio, setKeepRatio] = useState(true);
  const [outputFormat, setOutputFormat] = useState<ImageOutputFormat>('image/png');
  const [quality, setQuality] = useState(0.9);
  const [result, setResult] = useState<ProcessedFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedAspect = aspectByPreset[cropPreset];

  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const loadImageFile = useCallback(
    (file: File) => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);

      const nextUrl = URL.createObjectURL(file);
      sourceUrlRef.current = nextUrl;
      previewUrlRef.current = null;
      imageElement.current = null;
      setSourceFile(file);
      setSourceUrl(nextUrl);
      setPreviewUrl(null);
      setResult(null);
      setError(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
      trackAnalyticsEvent('image_upload', {
        mimeType: file.type,
        size: file.size,
      });
    },
    [],
  );

  useEffect(() => {
    const file = initialUpload?.files[0];
    if (!initialUpload || !file || consumedUploadIdRef.current === initialUpload.id) {
      return;
    }

    consumedUploadIdRef.current = initialUpload.id;
    loadImageFile(file);
    onUploadConsumed();
  }, [initialUpload, loadImageFile, onUploadConsumed]);

  const onImageSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    loadImageFile(file);
    event.target.value = '';
  };

  const onImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    imageElement.current = event.currentTarget;
    setImageSize({ width: naturalWidth, height: naturalHeight });
    setResizeWidth(naturalWidth);
    setResizeHeight(naturalHeight);
    setCrop(getPresetCrop(naturalWidth, naturalHeight, cropPreset));
  };

  const onResizeWidthChange = (value: number | '') => {
    setResizeWidth(value);
    if (keepRatio && typeof value === 'number' && imageSize.width > 0) {
      setResizeHeight(Math.max(1, Math.round((value / imageSize.width) * imageSize.height)));
    }
  };

  const onResizeHeightChange = (value: number | '') => {
    setResizeHeight(value);
    if (keepRatio && typeof value === 'number' && imageSize.height > 0) {
      setResizeWidth(Math.max(1, Math.round((value / imageSize.height) * imageSize.width)));
    }
  };

  const processImage = async () => {
    if (!sourceFile) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const naturalCrop = getNaturalCrop(completedCrop, imageElement.current);
      const processed = await imageProcessor.process(sourceFile, {
        crop: naturalCrop,
        resizeWidth: typeof resizeWidth === 'number' ? resizeWidth : undefined,
        resizeHeight: typeof resizeHeight === 'number' ? resizeHeight : undefined,
        outputFormat,
        quality,
      });

      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      setResult(processed);
      const nextPreviewUrl = URL.createObjectURL(processed.blob);
      previewUrlRef.current = nextPreviewUrl;
      setPreviewUrl(nextPreviewUrl);
      trackAnalyticsEvent('image_process', {
        outputFormat,
        sourceSize: sourceFile.size,
        resultSize: processed.size,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unknown image processing error.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="workspace">
      <div className="upload-panel">
        <UploadBox
          title={t('uploadImage')}
          subtitle={t('dropImage')}
          label={t('chooseFile')}
          accept="image/png,image/jpeg,image/webp,image/avif,image/bmp"
          onChange={onImageSelected}
        />
        {sourceFile && (
          <div className="file-meta">
            <FileImage size={18} />
            <div>
              <strong>{sourceFile.name}</strong>
              <span>{formatFileSize(sourceFile.size)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="editor-panel">
        {sourceUrl ? (
          <ReactCrop
            crop={crop}
            aspect={selectedAspect}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
          >
            <img src={sourceUrl} alt={sourceFile?.name ?? ''} onLoad={onImageLoad} />
          </ReactCrop>
        ) : (
          <div className="empty-preview">
            <FileImage size={42} />
          </div>
        )}
      </div>

      <aside className="settings-panel">
        <ControlGroup icon={<Maximize2 size={18} />} title={t('resize')}>
          <div className="field-grid">
            <NumberField label={t('width')} value={resizeWidth} onChange={onResizeWidthChange} />
            <NumberField label={t('height')} value={resizeHeight} onChange={onResizeHeightChange} />
          </div>
          <label className="checkbox-row">
            <input checked={keepRatio} type="checkbox" onChange={(event) => setKeepRatio(event.target.checked)} />
            <span>{t('keepRatio')}</span>
          </label>
        </ControlGroup>

        <ControlGroup icon={<CropIcon size={18} />} title={t('crop')}>
          <label className="field-label">
            {t('preset')}
            <select
              value={cropPreset}
              onChange={(event) => {
                const nextPreset = event.target.value as CropPreset;
                setCropPreset(nextPreset);
                if (imageSize.width > 0 && imageSize.height > 0) {
                  setCrop(getPresetCrop(imageSize.width, imageSize.height, nextPreset));
                }
              }}
            >
              {cropPresets.map((preset) => (
                <option key={preset} value={preset}>
                  {preset === 'free' ? 'Free' : preset}
                </option>
              ))}
            </select>
          </label>
        </ControlGroup>

        <ControlGroup icon={<Archive size={18} />} title={t('format')}>
          <label className="field-label">
            {t('format')}
            <select value={outputFormat} onChange={(event) => setOutputFormat(event.target.value as ImageOutputFormat)}>
              {imageFormats.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label">
            {t('quality')} {(quality * 100).toFixed(0)}%
            <input
              max="1"
              min="0.1"
              step="0.05"
              type="range"
              value={quality}
              onChange={(event) => setQuality(Number(event.target.value))}
            />
          </label>
        </ControlGroup>

        <button className="primary-button full-width" type="button" disabled={!sourceFile || isProcessing} onClick={processImage}>
          <Sparkles size={18} />
          {isProcessing ? '...' : t('process')}
        </button>

        {error && <p className="error-text">{error}</p>}
        {result && (
          <ResultPanel
            file={result}
            previewUrl={previewUrl}
            onDownload={() => {
              trackAnalyticsEvent('image_download', {
                mimeType: result.mimeType,
                size: result.size,
              });
              downloadService.download(result);
            }}
            t={t}
          />
        )}
      </aside>
    </div>
  );
}

function FileWorkspace({
  conversionPair,
  initialUpload,
  onUploadConsumed,
  t,
}: {
  conversionPair: ConversionPair | null;
  initialUpload: PendingUpload | null;
  onUploadConsumed: () => void;
  t: (key: string) => string;
}) {
  const [profileId, setProfileId] = useState<ConversionProfileId>('image-to-webp');
  const [sourceFiles, setSourceFiles] = useState<SelectedFileItem[]>([]);
  const [results, setResults] = useState<BatchConversionItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compression, setCompression] = useState(0.35);
  const [progress, setProgress] = useState<ConversionProgress | null>(null);
  const sourceFilesRef = useRef<SelectedFileItem[]>([]);
  const consumedUploadIdRef = useRef<string | null>(null);
  const profiles = fileConversionService.profiles;
  const commonProfiles = getCommonProfiles(sourceFiles);
  const activeProfile = profiles.find((profile) => profile.id === profileId) ?? commonProfiles[0] ?? profiles[0];
  const successfulResults = results.flatMap((item) => (item.result ? [item.result] : []));
  const totalSize = sourceFiles.reduce((size, item) => size + item.file.size, 0);

  useEffect(() => {
    sourceFilesRef.current = sourceFiles;
  }, [sourceFiles]);

  useEffect(() => () => revokePreviewUrls(sourceFilesRef.current), []);

  const loadFiles = useCallback((nextFiles: File[]) => {
    if (nextFiles.length === 0) {
      return;
    }

    const nextItems = nextFiles.map((file) => ({
        id: `${file.name}-${file.lastModified}-${file.size}-${globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: isPreviewableImage(file) ? URL.createObjectURL(file) : undefined,
        profileId: getDefaultProfileForFile(file, conversionPair),
    }));
    const defaultProfileId = nextItems[0]?.profileId ?? getProfileIdByTargetExtension(conversionPair?.to ?? '') ?? 'image-to-webp';

    setSourceFiles((items) => [...items, ...nextItems]);
    setProfileId((current) => (sourceFilesRef.current.length === 0 ? defaultProfileId : current));
    setResults([]);
    setProgress(null);
    setError(null);

    trackAnalyticsEvent('file_upload', {
      count: nextFiles.length,
      profileId,
      totalSize: nextFiles.reduce((size, file) => size + file.size, 0),
    });
  }, [conversionPair, profileId]);

  useEffect(() => {
    if (!initialUpload || consumedUploadIdRef.current === initialUpload.id) {
      return;
    }

    consumedUploadIdRef.current = initialUpload.id;
    loadFiles(initialUpload.files);
    onUploadConsumed();
  }, [initialUpload, loadFiles, onUploadConsumed]);

  const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    loadFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const applyProfileToAll = (nextProfileId: ConversionProfileId) => {
    setProfileId(nextProfileId);
    setSourceFiles((items) =>
      items.map((item) => {
        const isAvailable = fileConversionService.getProfilesForFile(item.file).some((profile) => profile.id === nextProfileId);
        return isAvailable ? { ...item, profileId: nextProfileId } : item;
      }),
    );
    setResults([]);
    setError(null);
    trackAnalyticsEvent('conversion_profile_select', {
      profileId: nextProfileId,
    });
  };

  const updateFileProfile = (fileId: string, nextProfileId: ConversionProfileId) => {
    setSourceFiles((items) => items.map((item) => (item.id === fileId ? { ...item, profileId: nextProfileId } : item)));
    setResults([]);
    setError(null);
  };

  const removeFile = (fileId: string) => {
    setSourceFiles((items) => {
      const item = items.find((candidate) => candidate.id === fileId);
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }

      return items.filter((candidate) => candidate.id !== fileId);
    });
    setResults((items) => items.filter((item) => item.fileId !== fileId));
    setProgress(null);
    setError(null);
  };

  const clearFiles = () => {
    revokePreviewUrls(sourceFilesRef.current);
    setSourceFiles([]);
    setResults([]);
    setProgress(null);
    setError(null);
  };

  const convertFiles = async () => {
    if (sourceFiles.length === 0) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResults([]);
    setProgress({
      completed: 0,
      currentFileName: sourceFiles[0]?.file.name ?? '',
      total: sourceFiles.length,
    });

    try {
      const conversionOptions: ConversionOptions = { compression };
      const nextResults: BatchConversionItem[] = [];

      for (const [index, item] of sourceFiles.entries()) {
        setProgress({
          completed: index,
          currentFileName: item.file.name,
          total: sourceFiles.length,
        });

        try {
          const result = await fileConversionService.convert(item.file, item.profileId, conversionOptions);
          nextResults.push({
            fileId: item.id,
            fileName: item.file.name,
            profileId: item.profileId,
            result,
          });
        } catch (caught) {
          nextResults.push({
            fileId: item.id,
            fileName: item.file.name,
            profileId: item.profileId,
            error: getErrorMessage(caught),
          });
        }

        setResults([...nextResults]);
        setProgress({
          completed: index + 1,
          currentFileName: item.file.name,
          total: sourceFiles.length,
        });
      }

      trackAnalyticsEvent('file_convert', {
        count: sourceFiles.length,
        profileId: 'mixed',
        successCount: nextResults.filter((item) => item.result).length,
      });
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="file-converter">
      <section className="converter-upload-stage">
        <UploadBox
          title={t('uploadFiles')}
          subtitle={t('fileUploadHint')}
          label={t('chooseFile')}
          accept={getCombinedAccept(profiles)}
          multiple
          onChange={onFileSelected}
        />
        {sourceFiles.length > 0 && (
          <div className="converter-upload-stage__meta">
            <div>
              <strong>{t('selectedFilesCount').replace('{count}', String(sourceFiles.length))}</strong>
              <span>{formatFileSize(totalSize)}</span>
            </div>
            <div className="converter-upload-stage__actions">
              <button className="secondary-button" type="button" onClick={() => document.querySelector<HTMLInputElement>('.upload-box input')?.click()}>
                <UploadCloud size={17} />
                {t('chooseFile')}
              </button>
              <button className="secondary-button" type="button" onClick={clearFiles}>
                <X size={17} />
                {t('clearFiles')}
              </button>
            </div>
          </div>
        )}
      </section>

      {sourceFiles.length === 0 ? (
        <section className="converter-start-panel">
          <div>
            <UploadCloud size={28} />
            <strong>{t('chooseFormatAfterUpload')}</strong>
            <p>{t('chooseFormatAfterUploadText')}</p>
          </div>
        </section>
      ) : (
        <div className="converter-body">
          <section className="file-queue">
            <div className="file-queue__head">
              <div>
                <span>{t('conversionQueue')}</span>
                <h2>{t('selectedFilesTitle')}</h2>
              </div>
            </div>

            <div className="selected-file-list">
            {sourceFiles.map((item) => (
              <SelectedFileCard
                availableProfiles={fileConversionService.getProfilesForFile(item.file)}
                item={item}
                key={item.id}
                profiles={profiles}
                t={t}
                compression={compression}
                onRemove={removeFile}
                updateFileProfile={updateFileProfile}
              />
            ))}
            </div>
          </section>

          <aside className="conversion-sidebar">
            <div className="conversion-sidebar__head">
              <Archive size={20} />
              <div>
                <strong>{t('batchActions')}</strong>
                <span>{t('batchActionsText')}</span>
              </div>
            </div>

            {commonProfiles.length > 0 ? (
              <FormatCombobox
                profiles={commonProfiles}
                t={t}
                value={activeProfile.id}
                onChange={applyProfileToAll}
              />
            ) : (
              <p className="hint-text">{t('noCommonFormats')}</p>
            )}

            {sourceFiles.length > 0 && (
              <div className="estimated-total">
                <span>{t('estimatedTotalSize')}</span>
                <strong>{formatFileSize(getEstimatedTotalSize(sourceFiles, compression))}</strong>
              </div>
            )}

            <ControlGroup icon={<Settings size={18} />} title={t('compressionSettings')}>
              <label className="field-label">
                {t('compressionLevel').replace('{value}', String(Math.round(compression * 100)))}
                <input
                  max="1"
                  min="0"
                  step="0.05"
                  type="range"
                  value={compression}
                  onChange={(event) => setCompression(Number(event.target.value))}
                />
              </label>
              <p className="hint-text">{t('compressionHint')}</p>
            </ControlGroup>

            <button className="primary-button full-width" type="button" disabled={isProcessing} onClick={convertFiles}>
              <Sparkles size={18} />
              {isProcessing ? '...' : t('process')}
            </button>

            {progress && <ConversionProgressBar progress={progress} t={t} />}

            {error && <p className="error-text">{error}</p>}
            {results.length > 0 && (
              <BatchResultPanel
                items={results}
                onDownload={(file) => {
                  trackAnalyticsEvent('file_download', {
                    mimeType: file.mimeType,
                    profileId: 'mixed',
                    size: file.size,
                  });
                  downloadService.download(file);
                }}
                onDownloadAll={() => {
                  trackAnalyticsEvent('file_download_all', {
                    count: successfulResults.length,
                    profileId: 'mixed',
                    totalSize: successfulResults.reduce((size, file) => size + file.size, 0),
                  });
                  downloadService.downloadArchive(successfulResults);
                }}
                t={t}
              />
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

interface SelectedFileItem {
  id: string;
  file: File;
  previewUrl?: string;
  profileId: ConversionProfileId;
}

interface ConversionProgress {
  completed: number;
  currentFileName: string;
  total: number;
}

interface BatchConversionItem {
  fileId: string;
  fileName: string;
  profileId: ConversionProfileId;
  result?: ProcessedFile;
  error?: string;
}

interface UploadBoxProps {
  title: string;
  subtitle: string;
  label: string;
  accept?: string;
  multiple?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

function SelectedFileCard({
  availableProfiles,
  compression,
  item,
  profiles,
  t,
  onRemove,
  updateFileProfile,
}: {
  availableProfiles: ConversionProfile[];
  compression: number;
  item: SelectedFileItem;
  profiles: ConversionProfile[];
  t: (key: string) => string;
  onRemove: (fileId: string) => void;
  updateFileProfile: (fileId: string, profileId: ConversionProfileId) => void;
}) {
  const activeProfile = profiles.find((profile) => profile.id === item.profileId);
  const sourceFormat = getDisplayFileExtension(item.file);
  const sizeEstimate = fileConversionService.estimateOutputSize(item.file, item.profileId, { compression });

  return (
    <article className="selected-file-card">
      <button className="selected-file-card__remove" type="button" aria-label={t('removeFile')} onClick={() => onRemove(item.id)}>
        <X size={16} />
      </button>
      <div className="selected-file-card__preview">
        {item.previewUrl ? <img src={item.previewUrl} alt={item.file.name} /> : getCategoryIcon(activeProfile?.category ?? 'archive')}
      </div>
      <div className="selected-file-card__body">
        <strong title={item.file.name}>{item.file.name}</strong>
        <div className="selected-file-card__meta">
          <span>{sourceFormat}</span>
          <span>{formatFileSize(item.file.size)}</span>
        </div>
        {sizeEstimate ? <EstimatedSizeBadge estimate={sizeEstimate} t={t} /> : null}
        <FormatCombobox
          profiles={availableProfiles}
          t={t}
          value={item.profileId}
          onChange={(nextProfileId) => updateFileProfile(item.id, nextProfileId)}
        />
      </div>
    </article>
  );
}

function EstimatedSizeBadge({ estimate, t }: { estimate: SizeEstimate; t: (key: string) => string }) {
  return (
    <div className={`estimated-size estimated-size--${estimate.confidence}`}>
      <span>{t('estimatedSize')}</span>
      <strong>≈ {formatFileSize(estimate.bytes)}</strong>
    </div>
  );
}

function FormatCombobox({
  profiles,
  t,
  value,
  onChange,
}: {
  profiles: ConversionProfile[];
  t: (key: string) => string;
  value: ConversionProfileId;
  onChange: (profileId: ConversionProfileId) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [formatSearch, setFormatSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<ConversionCategory>('image');
  const [panelStyle, setPanelStyle] = useState<CSSProperties>();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedProfile = profiles.find((profile) => profile.id === value) ?? profiles[0];
  const profileGroups = useMemo(() => groupProfilesByCategory(profiles), [profiles]);
  const filteredGroups = useMemo(() => getFilteredProfileGroups(profileGroups, formatSearch, t), [formatSearch, profileGroups, t]);
  const activeGroup =
    filteredGroups.find((group) => group.category === activeCategory) ??
    filteredGroups.find((group) => group.category === selectedProfile?.category) ??
    filteredGroups[0];
  const updatePanelPosition = useCallback(() => {
    setPanelStyle(getFormatPanelStyle(rootRef.current, triggerRef.current));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen, updatePanelPosition]);

  return (
    <div className="format-combobox" ref={rootRef}>
      <button
        className="format-combobox__trigger"
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        onClick={() => {
          if (!isOpen) {
            setActiveCategory(selectedProfile?.category ?? filteredGroups[0]?.category ?? 'image');
            setPanelStyle(getFormatPanelStyle(rootRef.current, triggerRef.current));
          }

          setIsOpen((current) => !current);
        }}
      >
        <strong>{selectedProfile ? selectedProfile.outputExtension.toUpperCase() : t('chooseFormat')}</strong>
        <ChevronDown size={17} />
      </button>

      {isOpen &&
        createPortal(
          <div className="format-combobox__panel" ref={panelRef} style={panelStyle}>
            <label className="format-combobox__search">
              <Search size={16} />
              <input
                autoFocus
                type="search"
                value={formatSearch}
                placeholder={t('formatSearchPlaceholder')}
                onChange={(event) => setFormatSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setIsOpen(false);
                  }
                }}
              />
            </label>

            <div className="format-combobox__matrix">
              <div className="format-combobox__categories" role="listbox" aria-label={t('formatsByCategory')}>
                {filteredGroups.map((group) => (
                  <button
                    className={group.category === activeGroup?.category ? 'format-category-option is-active' : 'format-category-option'}
                    key={group.category}
                    type="button"
                    onClick={() => setActiveCategory(group.category)}
                  >
                    <span>{t(`category.${group.category}`)}</span>
                    <ChevronDown size={15} />
                  </button>
                ))}
              </div>

              <div className="format-combobox__formats">
                {activeGroup?.profiles.map((profile) => (
                  <button
                    className={profile.id === value ? 'format-option is-active' : 'format-option'}
                    disabled={!isSelectableProfile(profile)}
                    key={profile.id}
                    title={profile.requiresServer ? t('serverRequiredDesc') : t(profile.description)}
                    type="button"
                    onClick={() => {
                      if (!isSelectableProfile(profile)) {
                        return;
                      }

                      onChange(profile.id);
                      setIsOpen(false);
                    }}
                  >
                    {profile.outputExtension.toUpperCase()}
                  </button>
                ))}
                {filteredGroups.length === 0 && <small className="format-combobox__empty">{t('noFormatsFound')}</small>}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function getFormatPanelStyle(root: HTMLDivElement | null, trigger: HTMLButtonElement | null): CSSProperties | undefined {
  if (!root || !trigger || window.innerWidth <= 720) {
    return undefined;
  }

  const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const viewportOffsetLeft = window.visualViewport?.offsetLeft ?? 0;
  const viewportOffsetTop = window.visualViewport?.offsetTop ?? 0;
  const edgeGap = 12;
  const triggerRect = trigger.getBoundingClientRect();
  const maxPanelWidth = root.closest('.conversion-flow__card') ? 420 : 520;
  const panelWidth = Math.min(maxPanelWidth, viewportWidth - edgeGap * 2);
  const preferredPanelHeight = 320;
  const minPanelHeight = 180;
  const belowSpace = viewportHeight - triggerRect.bottom - edgeGap;
  const aboveSpace = triggerRect.top - edgeGap;
  const shouldOpenUp = belowSpace < preferredPanelHeight && aboveSpace > belowSpace;
  const left = Math.min(Math.max(triggerRect.left, viewportOffsetLeft + edgeGap), viewportOffsetLeft + viewportWidth - panelWidth - edgeGap);

  if (shouldOpenUp) {
    return {
      position: 'fixed',
      left,
      top: 'auto',
      bottom: viewportHeight - triggerRect.top + edgeGap + viewportOffsetTop,
      width: panelWidth,
      maxHeight: Math.max(minPanelHeight, aboveSpace - edgeGap),
    };
  }

  return {
    position: 'fixed',
    left,
    top: triggerRect.bottom + edgeGap + viewportOffsetTop,
    bottom: 'auto',
    width: panelWidth,
    maxHeight: Math.max(minPanelHeight, belowSpace - edgeGap),
  };
}

function FormatPicker({
  formats,
  t,
  value,
  onChange,
}: {
  formats: Array<{ category: ConversionCategory; extension: string }>;
  t: (key: string) => string;
  value: string;
  onChange: (extension: string) => void;
}) {
  const profiles = useMemo(() => formats.map((format) => toPickerProfile(format)), [formats]);
  const selectedProfileId = profiles.find((profile) => isSameFormat(profile.outputExtension, value))?.id ?? profiles[0]?.id ?? value;

  return (
    <FormatCombobox
      profiles={profiles}
      t={t}
      value={selectedProfileId}
      onChange={(profileId) => {
        const profile = profiles.find((candidate) => candidate.id === profileId);
        if (profile) {
          onChange(profile.outputExtension);
        }
      }}
    />
  );
}

function ConversionProgressBar({ progress, t }: { progress: ConversionProgress; t: (key: string) => string }) {
  const percent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <div className="conversion-progress">
      <div className="conversion-progress__head">
        <strong>{t('conversionProgress')}</strong>
        <span>{percent}%</span>
      </div>
      <div className="conversion-progress__track" aria-label={t('conversionProgress')} aria-valuemax={100} aria-valuemin={0} aria-valuenow={percent} role="progressbar">
        <span style={{ width: `${percent}%` }} />
      </div>
      <small>
        {progress.completed >= progress.total
          ? t('conversionProgressDone')
          : t('conversionProgressCurrent').replace('{file}', progress.currentFileName)}
      </small>
    </div>
  );
}

function UploadBox({ title, subtitle, label, accept, multiple = false, onChange }: UploadBoxProps) {
  return (
    <label className="upload-box">
      <input accept={accept} multiple={multiple} type="file" onChange={onChange} />
      <span className="upload-box__icon">
        <UploadCloud size={30} />
      </span>
      <strong>{title}</strong>
      <span>{subtitle}</span>
      <em>{label}</em>
    </label>
  );
}

function ControlGroup({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="control-group">
      <h3>
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | '';
  onChange: (value: number | '') => void;
}) {
  return (
    <label className="field-label">
      {label}
      <input
        min="1"
        type="number"
        value={value}
        onChange={(event) => onChange(event.target.value === '' ? '' : Number(event.target.value))}
      />
    </label>
  );
}

function ResultPanel({
  file,
  previewUrl,
  onDownload,
  t,
}: {
  file: ProcessedFile;
  previewUrl?: string | null;
  onDownload: () => void;
  t: (key: string) => string;
}) {
  return (
    <div className="result-panel">
      {previewUrl && <img src={previewUrl} alt={file.fileName} />}
      <div>
        <span className="result-panel__status">
          <CheckCircle2 size={16} />
          {t('convertedReady')}
        </span>
        <strong>{file.fileName}</strong>
        <small>{formatFileSize(file.size)}</small>
      </div>
      <button className="secondary-button" type="button" onClick={onDownload}>
        <Download size={17} />
        {t('download')}
      </button>
    </div>
  );
}

function BatchResultPanel({
  items,
  onDownload,
  onDownloadAll,
  t,
}: {
  items: BatchConversionItem[];
  onDownload: (file: ProcessedFile) => void;
  onDownloadAll: () => void;
  t: (key: string) => string;
}) {
  const readyItems = items.filter((item) => item.result);

  return (
    <div className="batch-result-panel">
      <div className="batch-result-panel__head">
        <span className="result-panel__status">
          <CheckCircle2 size={16} />
          {t('convertedFilesReady').replace('{count}', String(readyItems.length))}
        </span>
        <button className="secondary-button" type="button" disabled={readyItems.length === 0} onClick={onDownloadAll}>
          <Download size={17} />
          {t('downloadAll')}
        </button>
      </div>

      <div className="batch-result-list">
        {items.map((item) => (
          <div className={item.result ? 'batch-result-item' : 'batch-result-item is-error'} key={item.fileName}>
            <div>
              <strong>{item.result?.fileName ?? item.fileName}</strong>
              <small>{item.result ? formatFileSize(item.result.size) : item.error}</small>
            </div>
            {item.result && (
              <button className="secondary-button" type="button" onClick={() => onDownload(item.result!)}>
                <Download size={16} />
                {t('download')}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

function getPresetCrop(mediaWidth: number, mediaHeight: number, preset: CropPreset): Crop {
  const aspect = aspectByPreset[preset];

  if (!aspect) {
    return { unit: '%', x: 5, y: 5, width: 90, height: 90 };
  }

  return centerAspectCrop(mediaWidth, mediaHeight, aspect);
}

function getNaturalCrop(crop: PixelCrop | undefined, image: HTMLImageElement | null) {
  if (!crop || !image || crop.width <= 0 || crop.height <= 0) {
    return undefined;
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  return {
    x: Math.round(crop.x * scaleX),
    y: Math.round(crop.y * scaleY),
    width: Math.round(crop.width * scaleX),
    height: Math.round(crop.height * scaleY),
  };
}

function getInitialLanguage(): LanguageCode {
  const pathLanguage = getPathLanguage(window.location.pathname);
  if (pathLanguage) {
    return pathLanguage;
  }

  const queryLanguage = new URLSearchParams(window.location.search).get('lang');
  if (isLanguageCode(queryLanguage)) {
    return queryLanguage;
  }

  const savedLanguage = localStorage.getItem('kim-converter-language');
  if (isLanguageCode(savedLanguage)) {
    return savedLanguage;
  }

  const browserLanguage = navigator.language.slice(0, 2);
  return isLanguageCode(browserLanguage) ? browserLanguage : 'ru';
}

function getInitialTheme(): ThemeMode {
  const themeWasSelected = localStorage.getItem('kim-converter-theme-explicit') === 'true';
  const savedTheme = localStorage.getItem('kim-converter-theme');
  if (themeWasSelected && (savedTheme === 'light' || savedTheme === 'dark')) {
    return savedTheme;
  }

  return 'light';
}

function getInitialRoute(): AppRoute {
  const path = getRoutePathWithoutLanguage(window.location.pathname);
  if (getConversionPairFromPath(path)) {
    return 'files';
  }

  switch (path) {
    case routePaths.images:
      return 'images';
    case routePaths.files:
      return 'files';
    case routePaths.imageInfo:
      return 'imageInfo';
    case routePaths.fileInfo:
      return 'fileInfo';
    case routePaths.privacy:
      return 'privacy';
    case routePaths.home:
      return 'home';
    default:
      break;
  }

  switch (window.location.hash.replace('#', '')) {
    case 'image-editor':
      return 'images';
    case 'file-converter':
      return 'files';
    case 'tools-image-editor':
      return 'imageInfo';
    case 'tools-file-converter':
      return 'fileInfo';
    default:
      return 'home';
  }
}

function getLocalizedPath(route: AppRoute, language: LanguageCode, conversionPair?: ConversionPair | null): string {
  const prefix = language === 'ru' ? '' : `/${language}`;
  const routePath = route === 'files' && conversionPair ? `/${getConversionPairSlug(conversionPair)}` : routePaths[route];

  if (routePath === '/') {
    return prefix ? `${prefix}/` : '/';
  }

  return `${prefix}${routePath}`;
}

function getLocalizedConversionPath(slug: string, language: LanguageCode): string {
  const prefix = language === 'ru' ? '' : `/${language}`;
  return `${prefix}/${slug}`;
}

function handleRouteLinkClick(
  event: MouseEvent<HTMLAnchorElement>,
  route: AppRoute,
  navigateTo: (route: AppRoute) => void,
) {
  if (shouldUseNativeNavigation(event)) {
    return;
  }

  event.preventDefault();
  navigateTo(route);
}

function handleSectionLinkClick(
  event: MouseEvent<HTMLAnchorElement>,
  sectionId: string,
  scrollHomeSection: (sectionId: string) => void,
) {
  if (shouldUseNativeNavigation(event)) {
    return;
  }

  event.preventDefault();
  scrollHomeSection(sectionId);
}

function isElementInRevealViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

  return rect.top < viewportHeight * 0.96 && rect.bottom > 0;
}

function isStaggeredRevealElement(element: HTMLElement): boolean {
  return element.matches(
    [
      '.task-card',
      '.benefit-card',
      '.legal-grid article',
      '.converter-upload-stage',
      '.converter-start-panel',
      '.file-queue',
      '.conversion-sidebar',
      '.upload-panel',
      '.editor-panel',
      '.settings-panel',
    ].join(', '),
  );
}

function shouldUseNativeNavigation(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

function getPathLanguage(pathname: string): LanguageCode | null {
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? null;
  return isLanguageCode(firstSegment) ? firstSegment : null;
}

function getRoutePathWithoutLanguage(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (isLanguageCode(segments[0] ?? null)) {
    segments.shift();
  }

  return segments.length > 0 ? `/${segments.join('/')}` : '/';
}

function updateDocumentSeo(route: AppRoute, language: LanguageCode, conversionPair?: ConversionPair | null) {
  const seo = getRouteSeo(route, language, conversionPair);
  const canonicalUrl = `${siteUrl}${getLocalizedPath(route, language, conversionPair)}`;
  const imageAlt = getSocialImageAlt(language);

  document.title = seo.title;
  setMetaByName('description', seo.description);
  setMetaByName('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
  setMetaByName('application-name', 'Kim Converter');
  removeMetaByName('keywords');
  setMetaByProperty('og:type', 'website');
  setMetaByProperty('og:site_name', 'Kim Converter');
  setMetaByProperty('og:title', seo.title);
  setMetaByProperty('og:description', seo.description);
  setMetaByProperty('og:url', canonicalUrl);
  setMetaByProperty('og:image', `${siteUrl}/assets/hero-converter.png`);
  setMetaByProperty('og:image:alt', imageAlt);
  setMetaByProperty('og:locale', ogLocales[language]);
  setMetaByName('twitter:card', 'summary_large_image');
  setMetaByName('twitter:title', seo.title);
  setMetaByName('twitter:description', seo.description);
  setMetaByName('twitter:image', `${siteUrl}/assets/hero-converter.png`);
  setMetaByName('twitter:image:alt', imageAlt);

  setCanonicalLink(canonicalUrl);
  setAlternateLinks(route, conversionPair);
  setStructuredData(route, canonicalUrl, language, conversionPair);
}

function getRouteSeo(route: AppRoute, language: LanguageCode, conversionPair?: ConversionPair | null): RouteSeo {
  if (route === 'files' && conversionPair) {
    return {
      title: getConversionPairTitle(conversionPair, language),
      description: getConversionPairDescription(conversionPair, language),
    };
  }

  return routeSeo[language][route];
}

function getConversionPairTitle(pair: ConversionPair, language: LanguageCode): string {
  const from = pair.from.toUpperCase();
  const to = pair.to.toUpperCase();
  return language === 'ru'
    ? `Конвертировать ${from} в ${to} онлайн бесплатно - Kim Converter`
    : `Convert ${from} to ${to} online free - Kim Converter`;
}

function getConversionPairDescription(pair: ConversionPair, language: LanguageCode): string {
  const from = pair.from.toUpperCase();
  const to = pair.to.toUpperCase();
  return language === 'ru'
    ? `Бесплатный онлайн-конвертер ${from} в ${to}: загрузите файл, выберите сжатие, посмотрите примерный размер после конвертации и скачайте результат без водяных знаков.`
    : `Free online ${from} to ${to} converter: upload a file, choose compression, preview the estimated output size and download without watermarks.`;
}

function getConversionPairCategory(pair: ConversionPair): ConversionCategory {
  return indexedConversionPairs.find((candidate) => getConversionPairSlug(candidate) === getConversionPairSlug(pair))?.category ?? 'image';
}

function getRelatedConversionLinks(pair: ConversionPair): ConversionPair[] {
  const pairSlug = getConversionPairSlug(pair);
  const category = getConversionPairCategory(pair);
  const related = indexedConversionPairs.filter(
    (candidate) =>
      getConversionPairSlug(candidate) !== pairSlug &&
      (candidate.category === category || candidate.from === pair.from || candidate.to === pair.to),
  );

  return Array.from(new Map(related.map((candidate) => [getConversionPairSlug(candidate), candidate])).values());
}

function getSocialImageAlt(language: LanguageCode): string {
  return language === 'ru'
    ? 'Интерфейс Kim Converter для конвертации файлов и редактирования изображений'
    : 'Kim Converter interface for file conversion and image editing';
}

function getSeoFaqTitle(language: LanguageCode): string {
  return language === 'ru' ? 'Вопросы о конвертации файлов онлайн' : 'Questions about online file conversion';
}

function getSeoFaqItems(route: AppRoute, language: LanguageCode, conversionPair?: ConversionPair | null): SeoFaqItem[] {
  if (route === 'files' && conversionPair) {
    return getConversionPairFaq(conversionPair, language);
  }

  if (route === 'images') {
    return getImageEditorFaq(language);
  }

  if (route === 'files') {
    return getFileConverterFaq(language);
  }

  if (route !== 'home') {
    return [];
  }

  const homeFaq: Record<LanguageCode, SeoFaqItem[]> = {
    ru: [
        {
          question: 'Как найти нужный конвертер файлов на сайте?',
          answer:
            'Откройте Kim Converter, выберите конвертацию файлов или перейдите сразу на страницу нужной пары форматов: PNG to JPG, JPG to PNG, WebP to JPG, JSON to CSV, CSV to JSON и другие.',
        },
        {
          question: 'Файлы обрабатываются в браузере?',
          answer:
            'Большая часть операций выполняется локально в браузере. Для сложных форматов сайт может использовать серверный конвертер, если выбранный профиль требует такой обработки.',
        },
        {
          question: 'Можно ли пользоваться сервисом бесплатно?',
          answer:
            'Да. Основные инструменты Kim Converter доступны бесплатно, без водяных знаков и без обязательной регистрации.',
        },
      ],
    en: [
        {
          question: 'How do I find the converter I need?',
          answer:
            'Open Kim Converter, choose file conversion, or go directly to a format-pair page such as PNG to JPG, JPG to PNG, WebP to JPG, JSON to CSV or CSV to JSON.',
        },
        {
          question: 'Are files processed in the browser?',
          answer:
            'Most operations run locally in the browser. Complex formats can use the server converter when the selected profile requires it.',
        },
        {
          question: 'Is Kim Converter free?',
          answer: 'Yes. Core Kim Converter tools are free to use, without watermarks and without mandatory registration.',
        },
      ],
    de: [
      {
        question: 'Wie finde ich den passenden Dateikonverter?',
        answer:
          'Öffnen Sie Kim Converter, wählen Sie Dateikonvertierung oder gehen Sie direkt zu einer Formatseite wie PNG to JPG, WebP to JPG, JSON to CSV oder CSV to JSON.',
      },
      {
        question: 'Werden Dateien im Browser verarbeitet?',
        answer:
          'Viele Vorgänge laufen lokal im Browser. Komplexe Formate können den Server-Konverter verwenden, wenn das gewählte Profil ihn benötigt.',
      },
      {
        question: 'Ist Kim Converter kostenlos?',
        answer: 'Ja. Die wichtigsten Werkzeuge sind kostenlos, ohne Wasserzeichen und ohne Pflichtregistrierung.',
      },
    ],
    el: [
      {
        question: 'Πώς βρίσκω τον μετατροπέα που χρειάζομαι;',
        answer:
          'Ανοίξτε το Kim Converter, επιλέξτε μετατροπή αρχείων ή μεταβείτε απευθείας σε σελίδα μορφών όπως PNG to JPG, WebP to JPG, JSON to CSV ή CSV to JSON.',
      },
      {
        question: 'Τα αρχεία επεξεργάζονται στο πρόγραμμα περιήγησης;',
        answer:
          'Οι περισσότερες εργασίες γίνονται τοπικά στο πρόγραμμα περιήγησης. Σύνθετες μορφές μπορούν να χρησιμοποιήσουν μετατροπέα server όταν απαιτείται.',
      },
      {
        question: 'Είναι δωρεάν το Kim Converter;',
        answer: 'Ναι. Τα βασικά εργαλεία είναι δωρεάν, χωρίς υδατογράφημα και χωρίς υποχρεωτική εγγραφή.',
      },
    ],
    es: [
      {
        question: '¿Cómo encuentro el convertidor que necesito?',
        answer:
          'Abre Kim Converter, elige conversión de archivos o entra directamente en una página de formato como PNG to JPG, WebP to JPG, JSON to CSV o CSV to JSON.',
      },
      {
        question: '¿Los archivos se procesan en el navegador?',
        answer:
          'La mayoría de las operaciones se ejecutan localmente en el navegador. Los formatos complejos pueden usar el convertidor del servidor cuando el perfil lo requiere.',
      },
      {
        question: '¿Kim Converter es gratis?',
        answer: 'Sí. Las herramientas principales son gratuitas, sin marca de agua y sin registro obligatorio.',
      },
    ],
    fr: [
      {
        question: 'Comment trouver le convertisseur dont j’ai besoin ?',
        answer:
          'Ouvrez Kim Converter, choisissez la conversion de fichiers ou allez directement vers une page de format comme PNG to JPG, WebP to JPG, JSON to CSV ou CSV to JSON.',
      },
      {
        question: 'Les fichiers sont-ils traités dans le navigateur ?',
        answer:
          'La plupart des opérations se font localement dans le navigateur. Les formats complexes peuvent utiliser le convertisseur serveur si le profil choisi l’exige.',
      },
      {
        question: 'Kim Converter est-il gratuit ?',
        answer: 'Oui. Les outils principaux sont gratuits, sans filigrane et sans inscription obligatoire.',
      },
    ],
  };

  return homeFaq[language];
}

function getConversionPairFaq(pair: ConversionPair, language: LanguageCode): SeoFaqItem[] {
  const from = pair.from.toUpperCase();
  const to = pair.to.toUpperCase();

  const faqMap: Record<LanguageCode, SeoFaqItem[]> = {
    ru: [
        {
          question: `Как конвертировать ${from} в ${to} онлайн?`,
          answer:
            `Откройте страницу ${from} to ${to}, загрузите файл, выберите доступный профиль и скачайте результат после обработки. Для изображений можно дополнительно управлять качеством и сжатием.`,
        },
        {
          question: `Нужно ли устанавливать программу для ${from} в ${to}?`,
          answer:
            'Нет. Конвертация запускается в браузере, а сложные профили при необходимости обрабатываются серверным конвертером Kim Converter.',
        },
      ],
    en: [
        {
          question: `How do I convert ${from} to ${to} online?`,
          answer:
            `Open the ${from} to ${to} page, upload a file, choose an available profile and download the converted result. Image conversions also include quality and compression controls.`,
        },
        {
          question: `Do I need to install software for ${from} to ${to}?`,
          answer:
            'No. Conversion starts in the browser, and complex profiles use the Kim Converter server converter when required.',
        },
      ],
    de: [
      {
        question: `Wie konvertiere ich ${from} in ${to} online?`,
        answer:
          `Öffnen Sie die ${from} to ${to}-Seite, laden Sie eine Datei hoch, wählen Sie ein verfügbares Profil und laden Sie die konvertierte ${to}-Datei herunter.`,
      },
      {
        question: `Muss ich Software für ${from} in ${to} installieren?`,
        answer:
          'Nein. Die Konvertierung startet im Browser; komplexe Profile verwenden bei Bedarf den Kim Converter Server-Konverter.',
      },
    ],
    el: [
      {
        question: `Πώς μετατρέπω ${from} σε ${to} online;`,
        answer:
          `Ανοίξτε τη σελίδα ${from} to ${to}, ανεβάστε αρχείο, επιλέξτε διαθέσιμο προφίλ και κατεβάστε το αρχείο ${to}.`,
      },
      {
        question: `Χρειάζεται εγκατάσταση για ${from} σε ${to};`,
        answer:
          'Όχι. Η μετατροπή ξεκινά στο πρόγραμμα περιήγησης και τα σύνθετα προφίλ χρησιμοποιούν server όταν χρειάζεται.',
      },
    ],
    es: [
      {
        question: `¿Cómo convertir ${from} a ${to} online?`,
        answer:
          `Abre la página ${from} to ${to}, sube un archivo, elige un perfil disponible y descarga el archivo ${to} convertido.`,
      },
      {
        question: `¿Necesito instalar algo para ${from} a ${to}?`,
        answer:
          'No. La conversión se inicia en el navegador y los perfiles complejos usan el convertidor del servidor cuando es necesario.',
      },
    ],
    fr: [
      {
        question: `Comment convertir ${from} en ${to} en ligne ?`,
        answer:
          `Ouvrez la page ${from} to ${to}, importez un fichier, choisissez un profil disponible et téléchargez le fichier ${to} converti.`,
      },
      {
        question: `Faut-il installer un logiciel pour ${from} en ${to} ?`,
        answer:
          'Non. La conversion démarre dans le navigateur; les profils complexes utilisent le convertisseur serveur si nécessaire.',
      },
    ],
  };

  return faqMap[language];
}

function getImageEditorFaq(language: LanguageCode): SeoFaqItem[] {
  const faqMap: Record<LanguageCode, SeoFaqItem[]> = {
    ru: [
      {
        question: 'Можно ли изменить размер изображения онлайн?',
        answer:
          'Да. Загрузите изображение, задайте ширину, высоту, качество и формат экспорта. Kim Converter помогает подготовить PNG, JPEG, PNG или AVIF без водяного знака.',
      },
      {
        question: 'Можно ли обрезать фото под нужное соотношение?',
        answer: 'Да. В редакторе доступны свободная обрезка и готовые пропорции 1:1, 4:3, 16:9, 3:4 и 9:16.',
      },
    ],
    en: [
      {
        question: 'Can I resize an image online?',
        answer:
          'Yes. Upload an image, set width, height, quality and export format. Kim Converter prepares PNG, JPEG, AVIF or WebP without a watermark.',
      },
      {
        question: 'Can I crop a photo to a fixed ratio?',
        answer: 'Yes. The editor supports free crop plus 1:1, 4:3, 16:9, 3:4 and 9:16 crop presets.',
      },
    ],
    de: [
      {
        question: 'Kann ich ein Bild online verkleinern oder skalieren?',
        answer:
          'Ja. Laden Sie ein Bild hoch, setzen Sie Breite, Höhe, Qualität und Ausgabeformat für PNG, JPEG, AVIF oder WebP.',
      },
      {
        question: 'Kann ich ein Foto auf ein festes Seitenverhältnis zuschneiden?',
        answer: 'Ja. Der Editor unterstützt freie Zuschnitte sowie 1:1, 4:3, 16:9, 3:4 und 9:16.',
      },
    ],
    el: [
      {
        question: 'Μπορώ να αλλάξω μέγεθος εικόνας online;',
        answer:
          'Ναι. Ανεβάστε εικόνα, ορίστε πλάτος, ύψος, ποιότητα και μορφή εξόδου για PNG, JPEG, PNG ή AVIF.',
      },
      {
        question: 'Μπορώ να περικόψω φωτογραφία σε συγκεκριμένη αναλογία;',
        answer: 'Ναι. Ο editor υποστηρίζει ελεύθερη περικοπή και αναλογίες 1:1, 4:3, 16:9, 3:4 και 9:16.',
      },
    ],
    es: [
      {
        question: '¿Puedo cambiar el tamaño de una imagen online?',
        answer:
          'Sí. Sube una imagen, define ancho, alto, calidad y formato de salida para PNG, JPEG, AVIF o WebP.',
      },
      {
        question: '¿Puedo recortar una foto con proporción fija?',
        answer: 'Sí. El editor admite recorte libre y proporciones 1:1, 4:3, 16:9, 3:4 y 9:16.',
      },
    ],
    fr: [
      {
        question: 'Puis-je redimensionner une image en ligne ?',
        answer:
          'Oui. Importez une image, définissez largeur, hauteur, qualité et format de sortie pour PNG, JPEG, AVIF ou WebP.',
      },
      {
        question: 'Puis-je recadrer une photo avec un ratio fixe ?',
        answer: 'Oui. L’éditeur prend en charge le recadrage libre et les ratios 1:1, 4:3, 16:9, 3:4 et 9:16.',
      },
    ],
  };

  return faqMap[language];
}

function getFileConverterFaq(language: LanguageCode): SeoFaqItem[] {
  const faqMap: Record<LanguageCode, SeoFaqItem[]> = {
    ru: [
      {
        question: 'Какие форматы поддерживает конвертер файлов?',
        answer:
          'Kim Converter работает с изображениями, таблицами CSV и JSON, архивами ZIP, а также с аудио, видео и документами по мере доступности выбранного профиля конвертации.',
      },
      {
        question: 'Есть ли отдельные страницы под запросы PNG в JPG и JSON в CSV?',
        answer:
          'Да. Для популярных направлений есть прямые страницы, например PNG to JPG, WebP to JPG, JSON to CSV и CSV to JSON.',
      },
    ],
    en: [
      {
        question: 'Which formats does the file converter support?',
        answer:
          'Kim Converter supports images, CSV and JSON tables, ZIP archives, and selected audio, video and document conversion profiles.',
      },
      {
        question: 'Are there direct pages for queries like PNG to JPG and JSON to CSV?',
        answer:
          'Yes. Popular conversions have direct pages such as PNG to JPG, WebP to JPG, JSON to CSV and CSV to JSON.',
      },
    ],
    de: [
      {
        question: 'Welche Formate unterstützt der Dateikonverter?',
        answer:
          'Kim Converter unterstützt Bilder, CSV- und JSON-Tabellen, ZIP-Archive sowie ausgewählte Audio-, Video- und Dokumentprofile.',
      },
      {
        question: 'Gibt es direkte Seiten für PNG to JPG oder JSON to CSV?',
        answer: 'Ja. Beliebte Konvertierungen haben eigene Seiten wie PNG to JPG, WebP to JPG, JSON to CSV und CSV to JSON.',
      },
    ],
    el: [
      {
        question: 'Ποιες μορφές υποστηρίζει ο μετατροπέας;',
        answer:
          'Το Kim Converter υποστηρίζει εικόνες, CSV και JSON, ZIP και επιλεγμένα προφίλ ήχου, βίντεο και εγγράφων.',
      },
      {
        question: 'Υπάρχουν ξεχωριστές σελίδες για PNG to JPG ή JSON to CSV;',
        answer: 'Ναι. Δημοφιλείς μετατροπές έχουν δικές τους σελίδες όπως PNG to JPG, WebP to JPG, JSON to CSV και CSV to JSON.',
      },
    ],
    es: [
      {
        question: '¿Qué formatos admite el convertidor?',
        answer:
          'Kim Converter admite imágenes, tablas CSV y JSON, archivos ZIP y perfiles seleccionados de audio, vídeo y documentos.',
      },
      {
        question: '¿Hay páginas directas para PNG to JPG o JSON to CSV?',
        answer: 'Sí. Las conversiones populares tienen páginas propias como PNG to JPG, WebP to JPG, JSON to CSV y CSV to JSON.',
      },
    ],
    fr: [
      {
        question: 'Quels formats le convertisseur prend-il en charge ?',
        answer:
          'Kim Converter prend en charge les images, CSV et JSON, les archives ZIP et certains profils audio, vidéo et documents.',
      },
      {
        question: 'Existe-t-il des pages directes pour PNG to JPG ou JSON to CSV ?',
        answer: 'Oui. Les conversions populaires ont leurs pages, par exemple PNG to JPG, WebP to JPG, JSON to CSV et CSV to JSON.',
      },
    ],
  };

  return faqMap[language];
}

function setMetaByName(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.append(element);
  }

  element.content = content;
}

function removeMetaByName(name: string) {
  document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.remove();
}

function setMetaByProperty(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.append(element);
  }

  element.content = content;
}

function setCanonicalLink(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.append(element);
  }

  element.href = href;
}

function setAlternateLinks(route: AppRoute, conversionPair?: ConversionPair | null) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());

  languages.forEach(({ code }) => {
    const element = document.createElement('link');
    element.rel = 'alternate';
    element.hreflang = code;
    element.href = `${siteUrl}${getLocalizedPath(route, code, conversionPair)}`;
    document.head.append(element);
  });

  const defaultElement = document.createElement('link');
  defaultElement.rel = 'alternate';
  defaultElement.hreflang = 'x-default';
  defaultElement.href = `${siteUrl}${getLocalizedPath(route, 'ru', conversionPair)}`;
  document.head.append(defaultElement);
}

function setStructuredData(route: AppRoute, canonicalUrl: string, language: LanguageCode, conversionPair?: ConversionPair | null) {
  const element = document.getElementById('structured-data');
  if (!element) {
    return;
  }

  const seo = getRouteSeo(route, language, conversionPair);
  const faqItems = getSeoFaqItems(route, language, conversionPair);
  const routeName = route === 'privacy' ? seo.title : 'Kim Converter';
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Kim Converter',
      item: `${siteUrl}/`,
    },
  ];

  if (route !== 'home') {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: seo.title,
      item: canonicalUrl,
    });
  }

  const appData =
    route === 'privacy'
      ? {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          name: routeName,
          url: canonicalUrl,
          description: seo.description,
          inLanguage: language,
          isPartOf: { '@id': `${siteUrl}/#website` },
        }
      : {
          '@type': 'WebApplication',
          '@id': `${canonicalUrl}#webapp`,
          name: routeName,
          url: canonicalUrl,
          description: seo.description,
          inLanguage: language,
          applicationCategory: 'MultimediaApplication',
          operatingSystem: 'Any',
          browserRequirements: 'Requires a modern browser with JavaScript enabled',
          isAccessibleForFree: true,
          image: `${siteUrl}/assets/hero-converter.png`,
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          featureList: [
            'Image resizing',
            'Image crop',
            'Image format conversion',
            'Estimated output file size',
            ...indexedConversionPairs.slice(0, 40).map((pair) => `${pair.from.toUpperCase()} to ${pair.to.toUpperCase()} conversion`),
            'CSV and JSON conversion',
            'ZIP compression',
          ],
        };

  const graph: Array<Record<string, unknown>> = [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: 'Kim Converter',
      url: `${siteUrl}/`,
      inLanguage: language,
    },
    appData,
    {
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumb`,
      itemListElement: breadcrumbItems,
    },
  ];

  if (route === 'home') {
    graph.push({
      '@type': 'ItemList',
      '@id': `${canonicalUrl}#popular-converters`,
      name: language === 'ru' ? 'Популярные конвертеры файлов' : 'Popular file converters',
      itemListElement: popularConversionLinks.map((link, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: link.label,
        url: `${siteUrl}${getLocalizedConversionPath(link.slug, language)}`,
      })),
    });
  }

  if (faqItems.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${canonicalUrl}#faq`,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  element.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
}

function getCombinedAccept(profiles: ConversionProfile[]): string | undefined {
  const accept = profiles
    .map((profile) => profile.accept)
    .filter((value) => value !== '*')
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

  return accept.length > 0 ? Array.from(new Set(accept)).join(',') : undefined;
}

function getDefaultProfileForFile(file: File, conversionPair: ConversionPair | null): ConversionProfileId {
  if (conversionPair) {
    const targetProfile = fileConversionService
      .getProfilesForFile(file)
      .find((profile) => isSameFormat(profile.outputExtension, conversionPair.to) && isSelectableProfile(profile));

    if (targetProfile) {
      return targetProfile.id;
    }
  }

  return fileConversionService.getDefaultProfileForFile(file);
}

function getProfileIdByTargetExtension(extension: string): ConversionProfileId | null {
  return fileConversionService.profiles.find((profile) => isSameFormat(profile.outputExtension, extension) && isSelectableProfile(profile))?.id ?? null;
}

function isSameFormat(left: string, right: string): boolean {
  const normalizedLeft = normalizeFormatAlias(left);
  const normalizedRight = normalizeFormatAlias(right);
  return normalizedLeft === normalizedRight || left.toLowerCase() === right.toLowerCase();
}

function normalizeFormatAlias(value: string): string {
  const normalized = value.toLowerCase();
  return normalized === 'jpeg' ? 'jpg' : normalized === 'tif' ? 'tiff' : normalized;
}

function getDisplayFileExtension(file: File): string {
  const extension = getFileExtensionFromName(file.name);
  if (extension) {
    return extension.toUpperCase();
  }

  const mimeExtension = file.type.split('/')[1]?.split(';')[0];
  return mimeExtension ? mimeExtension.toUpperCase() : 'FILE';
}

function getFileExtensionFromName(fileName: string): string {
  const extension = fileName.split('.').pop();
  return extension && extension !== fileName ? extension.toLowerCase() : '';
}

function getFileNameFromUrl(value: string, mimeType: string): string {
  try {
    const url = new URL(value);
    const pathName = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() ?? '');
    if (pathName && /\.[a-z0-9]{1,8}$/i.test(pathName)) {
      return pathName;
    }
  } catch {
    // Fall back to a generated name for invalid or unusual URLs.
  }

  const extension = mimeType.split('/')[1]?.split(';')[0]?.replace(/[^a-z0-9]/gi, '') || 'bin';
  return `remote-file.${extension}`;
}

async function fetchRemoteFile(url: string): Promise<File> {
  const directResponse = await fetch(url).catch(() => null);
  if (directResponse?.ok) {
    return responseToFile(directResponse, url);
  }

  const proxiedResponse = await fetch(`/api/import-url?url=${encodeURIComponent(url)}`);
  if (!proxiedResponse.ok) {
    throw new Error(`Remote import failed with status ${proxiedResponse.status}.`);
  }

  return responseToFile(proxiedResponse, url);
}

async function responseToFile(response: Response, sourceUrl: string): Promise<File> {
  const blob = await response.blob();
  const fileName = getFileNameFromContentDisposition(response.headers.get('content-disposition')) ?? getFileNameFromUrl(sourceUrl, blob.type);

  return new File([blob], fileName, {
    type: blob.type || 'application/octet-stream',
    lastModified: Date.now(),
  });
}

function getFileNameFromContentDisposition(disposition: string | null): string | null {
  if (!disposition) {
    return null;
  }

  const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1]);
  }

  const quotedMatch = /filename="([^"]+)"/i.exec(disposition);
  return quotedMatch?.[1] ?? null;
}

function getCommonProfiles(items: SelectedFileItem[]): ConversionProfile[] {
  if (items.length === 0) {
    return [];
  }

  const [firstItem, ...remainingItems] = items;
  const commonIds = new Set(fileConversionService.getProfilesForFile(firstItem.file).map((profile) => profile.id));

  remainingItems.forEach((item) => {
    const itemProfileIds = new Set(fileConversionService.getProfilesForFile(item.file).map((profile) => profile.id));
    Array.from(commonIds).forEach((profileId) => {
      if (!itemProfileIds.has(profileId)) {
        commonIds.delete(profileId);
      }
    });
  });

  return fileConversionService.profiles.filter((profile) => commonIds.has(profile.id));
}

function getEstimatedTotalSize(items: SelectedFileItem[], compression: number): number {
  return items.reduce((total, item) => {
    const estimate = fileConversionService.estimateOutputSize(item.file, item.profileId, { compression });
    return total + (estimate?.bytes ?? item.file.size);
  }, 0);
}

function getConversionFlowInputFormats(): Array<{ category: ConversionCategory; extension: string }> {
  return [
    ...['png', 'jpg', 'jpeg', 'avif', 'webp', 'gif', 'bmp', 'svg'].map((extension) => ({ category: 'image' as const, extension })),
    ...['mp3', 'wav', 'm4a', 'ogg', 'flac'].map((extension) => ({ category: 'audio' as const, extension })),
    ...['mp4', 'webm', 'mov', 'mkv', 'avi'].map((extension) => ({ category: 'video' as const, extension })),
    ...['txt', 'doc', 'docx', 'pdf'].map((extension) => ({ category: 'document' as const, extension })),
    ...['json', 'csv'].map((extension) => ({ category: 'data' as const, extension })),
  ];
}

function getConversionFlowOutputFormats(inputExtension: string): Array<{ category: ConversionCategory; extension: string }> {
  const pair = indexedConversionPairs.find((candidate) => isSameFormat(candidate.from, inputExtension));
  const inputCategory = pair?.category ?? 'image';
  const outputs = indexedConversionPairs
    .filter((candidate) => isSameFormat(candidate.from, inputExtension))
    .map((candidate) => ({ category: candidate.to === 'zip' ? ('archive' as const) : candidate.category, extension: candidate.to }));
  if (outputs.length > 0) {
    return Array.from(new Map(outputs.map((output) => [output.extension, output])).values());
  }

  const uniqueOutputs = ['png', 'jpg', 'avif', 'webp', 'pdf', 'zip'];
  return uniqueOutputs.map((extension) => ({ category: extension === 'zip' ? 'archive' : inputCategory, extension }));
}

function toPickerProfile(format: { category: ConversionCategory; extension: string }): ConversionProfile {
  const extension = format.extension.toLowerCase();

  return {
    id: `picker-${format.category}-${extension}`,
    title: extension.toUpperCase(),
    description: extension.toUpperCase(),
    accept: '*',
    category: format.category,
    outputExtension: extension,
    outputMimeType: 'application/octet-stream',
    browserSupported: true,
  };
}

function groupProfilesByCategory(profiles: ConversionProfile[]): Array<{ category: ConversionCategory; profiles: ConversionProfile[] }> {
  const categories: ConversionCategory[] = ['image', 'audio', 'video', 'document', 'data', 'archive'];

  return categories
    .map((category) => ({
      category,
      profiles: profiles.filter((profile) => profile.category === category),
    }))
    .filter((group) => group.profiles.length > 0);
}

function getFilteredProfileGroups(
  profileGroups: Array<{ category: ConversionCategory; profiles: ConversionProfile[] }>,
  search: string,
  t: (key: string) => string,
): Array<{ category: ConversionCategory; profiles: ConversionProfile[] }> {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return profileGroups;
  }

  return profileGroups
    .map((group) => ({
      category: group.category,
      profiles: group.profiles.filter((profile) => {
        const haystack = [
          t(profile.title),
          t(profile.description),
          t(`category.${profile.category}`),
          profile.outputExtension,
          profile.outputMimeType,
        ]
          .join(' ')
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      }),
    }))
    .filter((group) => group.profiles.length > 0);
}

function isSelectableProfile(profile: ConversionProfile): boolean {
  return profile.browserSupported || profile.requiresServer === true;
}

function isPreviewableImage(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return file.type.startsWith('image/') || extension === 'svg';
}

function revokePreviewUrls(items: SelectedFileItem[]): void {
  items.forEach((item) => {
    if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
}

function getCategoryIcon(category: ConversionCategory): ReactNode {
  switch (category) {
    case 'image':
      return <FileImage size={18} />;
    case 'audio':
      return <FileAudio size={18} />;
    case 'video':
      return <FileVideo size={18} />;
    case 'document':
    case 'data':
      return <FileText size={18} />;
    case 'archive':
      return <Archive size={18} />;
    default:
      return <Files size={18} />;
  }
}

function getErrorMessage(caught: unknown): string {
  return caught instanceof Error ? caught.message : 'Unknown file conversion error.';
}

function isLanguageCode(value: string | null): value is LanguageCode {
  return value !== null && supportedLanguageCodes.has(value as LanguageCode);
}

function buildPairs(category: ConversionCategory, fromFormats: string[], toFormats: string[]): IndexedConversionPair[] {
  return fromFormats.flatMap((from) =>
    toFormats
      .filter((to) => normalizeFormatAlias(to) !== normalizeFormatAlias(from))
      .map((to) => ({
        category,
        from,
        to,
      })),
  );
}

function getConversionPairFromPath(path: string): ConversionPair | null {
  const slug = path.replace(/^\//, '').toLowerCase();
  const match = /^([a-z0-9]+)-to-([a-z0-9]+)$/.exec(slug);

  if (!match) {
    return null;
  }

  const pair = { from: match[1], to: match[2] };
  return indexedConversionPairSlugs.has(getConversionPairSlug(pair)) ? pair : null;
}

function getConversionPairSlug(pair: ConversionPair): string {
  return `${pair.from.toLowerCase()}-to-${pair.to.toLowerCase()}`;
}
